import { inject, Component, OnInit, Input, HostBinding, HostListener, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import * as d3 from 'd3';
import { FilterService } from 'src/app/services/filter.service';
import { NoDataNotifierService } from 'src/app/services/no-data-notifier.service';

@Component({
    selector: 'app-funnel-chart',
    templateUrl: './funnel-chart.component.html',
    styleUrls: ['./funnel-chart.component.scss'],
    standalone: false
})
export class FunnelChartComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly noDataNotifier = inject(NoDataNotifierService);

  @ViewChild('funnelChartContainer', { static: true }) funnelChartContainer!: ElementRef;
  @ViewChild('funnel', { static: true }) funnelRoot!: ElementRef;
  @ViewChild('fs') fs!: ElementRef;
  @Input() data: any = [];
  @Input() config: any = {};
  @Input() heading: any;
  @Input() item: any;
  @Input() pageKey: any;
  @Input() headerConfig: any;
  @Input() error_name: any;

  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isActive = false;
  iconList: any = [];
  noData: Boolean = false;
  isLoading: boolean = true;
  initiateChart: Boolean = false;
  dynamicDisplayMode: 'multi' | 'super' = 'multi';
  hasDynamicColumns = false;

  chartData: any = [];
  props: any = {};
  divId: any = 'funnelChart';
  currentVisibleData: any = [];

  private cardWidth = 200;
  private cardHeight = 80;
  private colWidth = 240;
  private chartHeight = 500;
  private MIN_CHART_HEIGHT = 300;

  private wrapper: any;
  private svg: any;
  private linkGroup: any;
  private lineGroup: any;
  private gripsGroup: any;
  private cardsContainer: any;
  private dropdownEl: any;
  private groupByPopupEl: any;

  private allColumns: any[] = [];
  private nodes: any[] = [];
  private masterLinks: any[] = [];
  private breakdownData: any = {};
  private nodeAgg = new Map<string, { title: string, vol: number, columnId: string, stageSeq: number, rows: any[] }>();

  private adjList = new Map<string, string[]>();
  private nodeMap = new Map<string, any>();

  private expandedCards = new Map<string, string>();
  private openDropdowns = new Set<string>();
  private cardSearchTerms = new Map<string, string>();
  private zCounter = 100;
  private linkTooltipEl: any;

  // Per-link channel X overrides (split links). Key = `${source}->${target}`
  private linkChannelOverrides = new Map<string, number>();
  // Per-corridor channel X overrides (whole-bundle moves). Key = `${leftColId}_${rightColId}`
  private corridorChannelOverrides = new Map<string, number>();
  // Per-link Y overrides for split-port links. Key = `${source}->${target}`
  private linkSourcePortOverrides = new Map<string, number>();
  private linkTargetPortOverrides = new Map<string, number>();
  // Per-card Y overrides for whole-bundle moves. Key = node id
  private cardSourcePortOverrides = new Map<string, number>();
  private cardTargetPortOverrides = new Map<string, number>();
  // Per-card slot order overrides. Key = card id, value = link keys in display
  // order (top → bottom). Used to override the default neighbor-Y sort.
  private cardOutgoingSlotOrder = new Map<string, string[]>();
  private cardIncomingSlotOrder = new Map<string, string[]>();
  // Drag state
  private isDraggingLink = false;
  // Cross-filter state (breakdown row hover/click → highlight links)
  private crossFilterCardId: string | null = null;
  private crossFilterField: string | null = null;
  private crossFilterValue: string | null = null;
  private crossFilterHover: string | null = null;
  private crossFilterVolMap: Map<string, number> | null = null;

  private showTooltip(myType: any, myData: any, myX: any, myY: any, chartWidth: any): void {}
  private hideTooltip(myType: any): void {}

  constructor(private filterService: FilterService) {
    this.filterService.filterQuery.subscribe(() => {
      this.isLoading = true;
    });
  }

  ngOnInit(): void {
    this.initiateCharts();
  }

  ngAfterViewInit(): void {
    // Defer so the DOM layout has fully settled before measuring
    setTimeout(() => {
      this.chartHeight = this.calculateAvailableHeight();
      if (this.chartData.length > 0) {
        this.plotChart();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data && this.data.length > 0) {
        this.noData = false;
        this.getChartData();
      } else if (this.data && this.data.length === 0) {
        this.noData = true; this.noDataNotifier.report('funnel-chart');
        this.isLoading = false;
      }
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.isFullscreen) {
      this.chartHeight = this.calculateAvailableHeight();
    }
    this.plotChart();
  }

  private calculateAvailableHeight(): number {
    // Fixed chart height — keeps the chart size consistent across initial
    // load and filter refreshes (viewport-relative measurements were
    // producing different results at different lifecycle moments).
    return 500;
  }

  private calculateAvailableWidth(): number {
    // Measure the scroll-wrapper's inner width (the actual space the chart
    // renders in) rather than the outer funnelChartContainer. The
    // .funnel-container between them has padding (24px 32px = 64px horizontal)
    // that makes the outer measurement wider than the usable area.
    const scrollWrapper = document.getElementById('scrollWrapper_' + this.divId);
    if (scrollWrapper && scrollWrapper.clientWidth > 0) {
      return Math.floor(scrollWrapper.clientWidth);
    }
    const el = this.funnelChartContainer?.nativeElement;
    if (!el) return 900;
    return Math.floor(el.getBoundingClientRect().width) || 900;
  }

  @HostListener('fullscreenchange', ['$event'])
  @HostListener('webkitfullscreenchange', ['$event'])
  @HostListener('mozfullscreenchange', ['$event'])
  @HostListener('MSFullscreenChange', ['$event'])
  screenChange(event: any) {
    if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

  fullscreenChanges(fullscreen: any) {
    if (fullscreen == false) {
      this.closeFullscreen();
      return;
    }
    this.chartHeight = window.outerHeight - 60;
    this.isFullscreen = true;
    this.isActive = true;
    this.plotChart();
  }

  closeFullscreen(): void {
    this.isFullscreen = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    this.isActive = false;
    setTimeout(() => {
      this.chartHeight = this.calculateAvailableHeight();
      this.plotChart();
    }, 100);
  }

  // ── STEP 1: initiateCharts() — One-time container scaffolding ──
  initiateCharts(): void {
    const myClass = this.divId;
    const container = d3.select('#' + myClass);

    const outerContainer = container.append('div')
      .attr('class', 'funnel-container')
      .attr('id', 'funnelContainer_' + myClass)
      .style('font-family', 'Poppins');

    const header = outerContainer.append('div')
      .attr('class', 'funnel-header');

    const headerActions = header.append('div')
      .attr('class', 'header-actions');

    const scrollWrapper = outerContainer.append('div')
      .attr('class', 'chart-scroll-wrapper')
      .attr('id', 'scrollWrapper_' + myClass);

    scrollWrapper.append('div')
      .attr('id', 'chartArea_' + myClass);
  }

  // ── STEP 2: plotChart() — Main Rendering Engine ──
  plotChart(): void {
    if (!this.chartData || !this.allColumns.length) return;

    const myClass = this.divId;
    const myChart = this;
    const props = this.props;
    const el = document.getElementById('chartArea_' + myClass);
    if (!el) return;

    d3.select(el).selectAll('*').remove();
    this.dismissDropdown();
    this.dismissGroupByPopup();
    this.expandedCards.clear();

    // Recompute dynamic column nodes based on current column positions
    this.recalculateOptionalVolumes();

    this.nodeMap = new Map(this.nodes.map(n => [n.id, n]));
    this.adjList = new Map<string, string[]>();
    this.masterLinks.forEach(l => {
      if (!this.adjList.has(l.source)) this.adjList.set(l.source, []);
      this.adjList.get(l.source)!.push(l.target);
    });

    const containerWidth = this.calculateAvailableWidth();
    const activeCols = this.getActiveColumns();
    // Minimum colWidth = cardWidth + 100, so the gap between adjacent column
    // cards is at least 100px. When the container is too narrow to fit all
    // columns at this minimum, the funnel-wrapper exceeds the container width
    // and the .chart-scroll-wrapper (overflow-x: auto in SCSS) shows a scrollbar.
    this.colWidth = Math.max(this.cardWidth + 100, containerWidth / activeCols.length);

    this.recalculatePositions();

    // Layout: position cards after X values are set by recalculatePositions
    this.layoutNodes(this.nodes, this.masterLinks);

    // Wrapper width: just enough to fit all cards with their inter-column gaps,
    // but at least as wide as the container so there's no gap on the right when
    // columns fit. When columns overflow (many cols), this exceeds containerWidth
    // and the chart-scroll-wrapper shows a horizontal scrollbar.
    const neededWidth = (activeCols.length - 1) * this.colWidth + this.cardWidth;
    const width = Math.max(containerWidth, neededWidth);

    this.wrapper = d3.select(el)
      .append('div')
      .attr('class', 'funnel-wrapper')
      .style('position', 'relative')
      .style('width', `${width}px`)
      .style('height', `${this.chartHeight + 100}px`)
      .style('font-family', 'Poppins');

    this.svg = this.wrapper.append('svg')
      .style('position', 'absolute')
      .style('top', '0')
      .style('left', '0')
      .style('width', '100%')
      .style('height', `${this.chartHeight}px`)
      .attr('pointer-events', 'none')
      .style('z-index', '1');

    this.linkGroup = this.svg.append('g').attr('class', 'links').attr('pointer-events', 'all');
    this.lineGroup = this.svg.append('g').attr('class', 'lines');
    this.gripsGroup = this.wrapper.append('div').attr('class', 'grips');
    this.cardsContainer = this.wrapper;

    this.renderLines(activeCols);
    this.renderGrips(activeCols);
    this.renderCards();
    this.updateLinks();

    myChart.currentVisibleData = [this.nodes, this.getActiveColumns()];
  }

  // ── STEP 3: getChartData() — Data configuration ──
  getChartData(): void {
    if (!this.data || this.data.length === 0) return;

    this.isLoading = false;
    this.noData = false;
    this.chartData = this.data;

    // Reset link drag overrides on data refresh
    this.linkChannelOverrides.clear();
    this.corridorChannelOverrides.clear();
    this.linkSourcePortOverrides.clear();
    this.linkTargetPortOverrides.clear();
    this.cardSourcePortOverrides.clear();
    this.cardTargetPortOverrides.clear();
    this.cardOutgoingSlotOrder.clear();
    this.cardIncomingSlotOrder.clear();
    this.crossFilterCardId = null;
    this.crossFilterField = null;
    this.crossFilterValue = null;
    this.crossFilterHover = null;
    this.crossFilterVolMap = null;

    this.props = {
      fixedCategories: this.config.fixedCategories || [],
      columnColours: this.config.columnColours || {
        'Intake':          '#4a7bed',
        'Payer Type':      '#805ad5',
        'Patient Type':    '#f56565',
        'BI Outcome':      '#ed64a6',
        'PA Process':      '#ecbf44',
        'PA Submission':   '#48bb78',
        'Treatement ready': '#1363DF',
      },
      colorMode: this.config.colorMode || 'palette',
      colours: this.config.colours || {
        'BI Submission': '#4a7bed',
        'Commercial': '#48bb78',
        'Government': '#ecc94b',
        'New Patients': '#f56565',
        'Existing Patients': '#805ad5',
        'PA Required by Policy': '#ed64a6',
        'No New PA Required': '#a0aec0',
        'No PA Required': '#ed8936',
        'Other PA Provider': '#ecbf44',
        'PA Initiated': '#e53e3e',
        'PA opt out': '#a0aec0',
        'PA submitted': '#4a7bed',
        'PA Not Submitted': '#a0aec0',
        'Denied': '#e53e3e',
        'Ready for treatment': '#48bb78',
      },
      columnPalettes: this.config.columnPalettes || {
        'Intake':          ['#4a7bed'],
        'Payer Type':      ['#805ad5', '#9b6edf', '#b589e8'],
        'Patient Type':    ['#f56565', '#f98080', '#fc9c9c'],
        'BI Outcome':      ['#ed64a6', '#d94e8f', '#c43878'],
        'PA Process':      ['#ecbf44', '#d4a93c', '#bc9334'],
        'PA Submission':   ['#48bb78', '#3da568', '#328f58'],
        'Treatement ready': ['#1363DF', '#0f4faf', '#0b3b7f'],
      },
      defaultColour: this.config.defaultColour || '#a0aec0',
      dynamicColumns: this.config.dynamicColumns || [
        { field: 'patient_type',       label: 'Patient Type' },
        { field: 'payer_type_name',    label: 'Payer Type' },
        { field: 'group_name',         label: 'Groups' },
        { field: 'practice',           label: 'Practices' },
        { field: 'practice_locn_name', label: 'Locations' },
      ],
      breakdownDimensions: this.config.breakdownDimensions || [
        { field: 'patient_type',       label: 'Patient Type' },
        { field: 'payer_type_name',    label: 'Payer Type' },
        { field: 'group_name',         label: 'Groups' },
        { field: 'practice',           label: 'Practices' },
        { field: 'practice_locn_name', label: 'Locations' },
      ],
      chartHeight: this.config.chartHeight || 500,
      cardWidth: this.config.cardWidth || 200,
      cardHeight: this.config.cardHeight || 80,
      colWidth: this.config.colWidth || 240,
    };
    this.props.filterOptions = this.props.breakdownDimensions.map((d: any) => d.label);

    const result = this.buildFunnelData(
      this.chartData,
      this.props.fixedCategories,
      this.props.dynamicColumns,
      this.props.columnColours,
      this.props.defaultColour,
      this.props.breakdownDimensions,
      this.props.colorMode,
      this.props.columnPalettes,
      this.props.colours
    );
    this.allColumns = result.allColumns;
    this.nodes = result.nodes;
    this.masterLinks = result.masterLinks;
    this.breakdownData = result.breakdownData;
    this.nodeAgg = result.nodeAgg;

    if (!this.isFullscreen) {
      this.chartHeight = this.calculateAvailableHeight();
    }
    if (this.props.cardWidth) this.cardWidth = this.props.cardWidth;
    if (this.props.cardHeight) this.cardHeight = this.props.cardHeight;

    // Compute whether dynamic columns exist (for the toggle UI)
    this.hasDynamicColumns = this.allColumns.some((c: any) => !c.fixed);

    // Make the chart container visible BEFORE plotting so calculateAvailableWidth()
    // can measure the actual container width. Both the Angular binding AND a
    // direct DOM style update — the binding flip won't take effect until the
    // next change-detection cycle, but plotChart() runs synchronously below.
    this.isLoading = false;
    if (this.funnelChartContainer?.nativeElement) {
      (this.funnelChartContainer.nativeElement as HTMLElement).style.display = '';
    }
    this.plotChart();
  }

  // ── Data transformation: flat rows → funnel structures ──

  private slugify(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
  }

  private desaturateColor(hex: string, index: number, total: number): string {
    // Convert hex → HSL, reduce saturation progressively, convert back
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    // Reduce saturation: first card = full, last card = 30% of original
    const factor = total <= 1 ? 0 : index / (total - 1) * 0.7;
    const newS = s * (1 - factor);
    // HSL → RGB
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + newS) : l + newS - l * newS;
    const p = 2 * l - q;
    const rr = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    const gg = Math.round(hue2rgb(p, q, h) * 255);
    const bb = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
    return `#${rr.toString(16).padStart(2, '0')}${gg.toString(16).padStart(2, '0')}${bb.toString(16).padStart(2, '0')}`;
  }

  private buildFunnelData(
    rawData: any[],
    fixedCategories: string[],
    dynamicColumnDefs: any[],
    columnColours: any,
    defaultColour: string,
    breakdownDims: any[],
    colorMode: string,
    columnPalettes: any,
    colours: any
  ): { allColumns: any[], nodes: any[], masterLinks: any[], breakdownData: any, nodeAgg: Map<string, any> } {

    // ── 1a. Derive FIXED columns from API category data ──
    const categoryMap = new Map<string, number>(); // category → cat_seq_no
    rawData.forEach((row: any) => {
      const cat = row.category;
      if (cat && !categoryMap.has(cat)) {
        categoryMap.set(cat, row.cat_seq_no || 0);
      }
    });
    const fixedSet = new Set(fixedCategories.map((c: string) => c.toLowerCase()));

    let activePosition = 0;
    const allColumns: any[] = [];

    // Only include API categories that are in the fixedCategories list
    Array.from(categoryMap.entries())
      .sort((a, b) => a[1] - b[1])
      .forEach(([category, seqNo]) => {
        const isFixed = fixedSet.size === 0 || fixedSet.has(category.toLowerCase());
        if (!isFixed) return; // Skip non-fixed API categories — dynamic columns replace them
        const colId = this.slugify(category);
        allColumns.push({
          id: colId,
          label: category,
          fixed: true,
          canonicalOrder: seqNo,
          active: true,
          position: activePosition++,
        });
      });

    // ── 1b. Add DYNAMIC columns from config (row-level grouping fields) ──
    const maxFixedOrder = allColumns.length > 0
      ? Math.max(...allColumns.map((c: any) => c.canonicalOrder))
      : 0;

    dynamicColumnDefs.forEach((def: any, idx: number) => {
      const colId = 'dyn_' + this.slugify(def.field);
      allColumns.push({
        id: colId,
        label: def.label,
        field: def.field,   // row-level field name
        fixed: false,
        canonicalOrder: maxFixedOrder + idx + 1,
        active: false,
        position: -1,
      });
    });

    // ── 2a. Derive FIXED nodes from API (category + node_name), summing count ──
    const nodeAgg = new Map<string, { title: string, vol: number, columnId: string, stageSeq: number, rows: any[] }>();
    const fixedColIds = new Set(allColumns.filter((c: any) => c.fixed).map((c: any) => c.id));

    rawData.forEach((row: any) => {
      const cat = row.category;
      const nodeName = row.node_name;
      if (!cat || !nodeName) return;
      const colId = this.slugify(cat);
      if (!fixedColIds.has(colId)) return; // Only process fixed category data
      const nodeId = colId + '__' + this.slugify(nodeName);
      if (!nodeAgg.has(nodeId)) {
        nodeAgg.set(nodeId, { title: nodeName, vol: 0, columnId: colId, stageSeq: row.stage_seq_no || 0, rows: [] });
      }
      const entry = nodeAgg.get(nodeId)!;
      entry.vol += (row.count || 0);
      entry.rows.push(row);
    });

    // ── 2b. Derive DYNAMIC nodes by grouping ALL rows by each dynamic field ──
    dynamicColumnDefs.forEach((def: any) => {
      const colId = 'dyn_' + this.slugify(def.field);
      const countMap = new Map<string, { vol: number, rows: any[] }>();

      rawData.forEach((row: any) => {
        const value = row[def.field];
        if (value === null || value === undefined || value === '') return;
        if (!countMap.has(value)) countMap.set(value, { vol: 0, rows: [] });
        const entry = countMap.get(value)!;
        entry.vol += (row.count || 0);
        entry.rows.push(row);
      });

      if (this.dynamicDisplayMode === 'super') {
        // Super card mode: ONE node per dynamic column with aggregate volume
        const allRows: any[] = [];
        let totalVol = 0;
        countMap.forEach((data) => { totalVol += data.vol; allRows.push(...data.rows); });
        const superNodeId = colId + '__super';
        const superEntry: any = { title: def.label, vol: totalVol, columnId: colId, stageSeq: 0, rows: allRows, isSuperCard: true, field: def.field };
        nodeAgg.set(superNodeId, superEntry);

        // Build per-value breakdown for the super card
        const dimLabel = this.props?.breakdownDimensions?.find((bd: any) => bd.field === def.field)?.label || def.label;
        const valueSorted = Array.from(countMap.entries()).sort((a, b) => b[1].vol - a[1].vol);
        const superBreakdown = valueSorted.map(([label, data], idx) => ({
          label,
          count: data.vol,
          pct: totalVol > 0 ? Math.round((data.vol / totalVol) * 100) : 0,
          color: defaultColour,
        }));
        breakdownData[superNodeId] = { [dimLabel]: superBreakdown };
      } else {
        // Multi-card mode: individual nodes, top 6 by volume
        let stageIdx = 0;
        Array.from(countMap.entries())
          .sort((a, b) => b[1].vol - a[1].vol)
          .slice(0, 6)
          .forEach(([value, data]) => {
            const nodeId = colId + '__' + this.slugify(value);
            if (!nodeAgg.has(nodeId)) {
              nodeAgg.set(nodeId, { title: value, vol: data.vol, columnId: colId, stageSeq: stageIdx++, rows: data.rows });
            }
          });
      }
    });

    // Build column totals for percentage calculation
    const columnTotals = new Map<string, number>();
    nodeAgg.forEach((entry) => {
      const current = columnTotals.get(entry.columnId) || 0;
      columnTotals.set(entry.columnId, current + entry.vol);
    });

    // Build column-to-palette mapping
    const colIdToLabel = new Map(allColumns.map((c: any) => [c.id, c.label]));

    const nodes: any[] = [];
    const nodesByCol = new Map<string, any[]>();
    nodeAgg.forEach((entry, nodeId) => {
      if (!nodesByCol.has(entry.columnId)) nodesByCol.set(entry.columnId, []);
      nodesByCol.get(entry.columnId)!.push({ nodeId, ...entry });
    });

    nodesByCol.forEach((colNodes, colId) => {
      colNodes.sort((a: any, b: any) => a.stageSeq - b.stageSeq);
      const columnTotal = columnTotals.get(colId) || 1;
      const spacing = this.chartHeight / (colNodes.length + 1);

      const colLabel = colIdToLabel.get(colId) || colId;
      const baseColor = columnColours[colLabel] || columnColours[colId] || defaultColour;
      const palette = columnPalettes[colLabel] || columnPalettes[colId] || [baseColor];

      colNodes.forEach((entry: any, idx: number) => {
        let cardColor: string;
        if (colorMode === 'classic') {
          cardColor = colours[entry.title] || defaultColour;
        } else if (colorMode === 'palette') {
          cardColor = idx < palette.length ? palette[idx] : palette[palette.length - 1];
        } else {
          cardColor = colNodes.length <= 1 ? baseColor : this.desaturateColor(baseColor, idx, colNodes.length);
        }
        nodes.push({
          id: entry.nodeId,
          title: entry.title,
          vol: entry.vol,
          pct: columnTotal > 0 ? Math.round((entry.vol / columnTotal) * 1000) / 10 : 0,
          columnId: colId,
          color: cardColor,
          y: spacing * (idx + 1),
        });
      });
    });

    // ── 3. Build links from parent_node_name (fixed columns only) ──
    const nameToNodeIds = new Map<string, string[]>();
    nodeAgg.forEach((entry, nodeId) => {
      if (!fixedColIds.has(entry.columnId)) return; // Only fixed nodes have parent links
      if (!nameToNodeIds.has(entry.title)) nameToNodeIds.set(entry.title, []);
      nameToNodeIds.get(entry.title)!.push(nodeId);
    });

    const colOrder = new Map(allColumns.map((c: any) => [c.id, c.canonicalOrder]));

    const linkMap = new Map<string, any>();
    const masterLinks: any[] = [];

    nodeAgg.forEach((entry, nodeId) => {
      if (!fixedColIds.has(entry.columnId)) return; // Links only between fixed columns
      const nodeColOrder = colOrder.get(entry.columnId);

      const parentCountMap = new Map<string, number>();
      entry.rows.forEach((row: any) => {
        if (row.parent_node_name) {
          const prev = parentCountMap.get(row.parent_node_name) || 0;
          parentCountMap.set(row.parent_node_name, prev + (row.count || 0));
        }
      });

      parentCountMap.forEach((vol: number, parentName: string) => {
        const candidateIds = nameToNodeIds.get(parentName) || [];
        for (const candidateId of candidateIds) {
          const candidateEntry = nodeAgg.get(candidateId);
          if (!candidateEntry) continue;
          const candidateOrder = colOrder.get(candidateEntry.columnId);
          if (candidateOrder !== undefined && nodeColOrder !== undefined && candidateOrder < nodeColOrder) {
            const key = candidateId + '->' + nodeId;
            if (!linkMap.has(key)) {
              const link = { source: candidateId, target: nodeId, vol: vol };
              linkMap.set(key, link);
              masterLinks.push(link);
            } else {
              linkMap.get(key)!.vol += vol;
            }
          }
        }
      });
    });

    // ── 4. Build per-node breakdown data ──
    const breakdownData: any = {};

    nodeAgg.forEach((entry, nodeId) => {
      const nodeBreakdowns: any = {};

      breakdownDims.forEach((dim: any) => {
        const countMap: any = {};
        let total = 0;

        entry.rows.forEach((row: any) => {
          const value = row[dim.field];
          if (value === null || value === undefined || value === '') return;
          const rowCount = row.count || 1;
          if (!countMap[value]) countMap[value] = 0;
          countMap[value] += rowCount;
          total += rowCount;
        });

        const colLabel = colIdToLabel.get(entry.columnId) || entry.columnId;
        const dimBase = columnColours[colLabel] || columnColours[entry.columnId] || defaultColour;
        const dimPalette = columnPalettes[colLabel] || columnPalettes[entry.columnId] || [dimBase];
        const sortedEntries = Object.entries(countMap).sort((a: any, b: any) => b[1] - a[1]);

        const items = sortedEntries.map(([label, count]: any, idx: number) => {
          let itemColor: string;
          if (colorMode === 'classic') {
            itemColor = colours[label] || defaultColour;
          } else if (colorMode === 'palette') {
            itemColor = idx < dimPalette.length ? dimPalette[idx] : dimPalette[dimPalette.length - 1];
          } else {
            itemColor = sortedEntries.length <= 1 ? dimBase : this.desaturateColor(dimBase, idx, sortedEntries.length);
          }
          return {
            label: label,
            count: count,
            pct: total > 0 ? Math.round((count / total) * 100) : 0,
            color: itemColor,
          };
        });

        nodeBreakdowns[dim.label] = items;
      });

      breakdownData[nodeId] = nodeBreakdowns;
    });

    return { allColumns, nodes, masterLinks, breakdownData, nodeAgg };
  }

  // ── Column helpers ──

  private getActiveColumns(): any[] {
    return this.allColumns
      .filter(c => c.active)
      .sort((a, b) => a.position - b.position);
  }

  private recalculatePositions(): void {
    const active = this.getActiveColumns();
    active.forEach((col, i) => {
      col.position = i;
      col.x = i * this.colWidth;
    });
    const colMap = new Map(this.allColumns.map(c => [c.id, c]));
    this.nodes.forEach(n => {
      const col = colMap.get(n.columnId);
      if (col && col.active) {
        n.x = col.x;
      }
    });
  }

  private getColumnForNode(nodeId: string): any | undefined {
    const node = this.nodeMap.get(nodeId);
    if (!node) return undefined;
    return this.allColumns.find(c => c.id === node.columnId);
  }

  private getColumnsByVisualOrder(): any[] {
    return this.allColumns
      .filter(c => c.active)
      .sort((a, b) => (a.x || 0) - (b.x || 0));
  }

  private getVisibleNodes(): any[] {
    const activeColumnIds = new Set(this.getActiveColumns().map(c => c.id));
    return this.nodes.filter(n => activeColumnIds.has(n.columnId));
  }

  private getInactiveOptionalColumns(): any[] {
    return this.allColumns.filter(c => !c.fixed && !c.active);
  }

  // ── BFS for link computation ──

  private bfsToTargets(startNodeId: string, targetNodeIds: Set<string>, excludeColumnIds: Set<string>): string[] {
    const results: string[] = [];
    const visited = new Set<string>();
    const queue = [startNodeId];
    visited.add(startNodeId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const neighbors = this.adjList.get(current) || [];
      for (const neighbor of neighbors) {
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);

        if (targetNodeIds.has(neighbor)) {
          results.push(neighbor);
          continue;
        }

        const col = this.getColumnForNode(neighbor);
        if (col && !excludeColumnIds.has(col.id)) {
          queue.push(neighbor);
        }
      }
    }
    return results;
  }

  private computeVisibleLinks(): any[] {
    const activeCols = this.getColumnsByVisualOrder();
    const visibleLinks: any[] = [];

    // Build vol lookup from masterLinks
    const masterVolMap = new Map<string, number>();
    this.masterLinks.forEach(l => {
      masterVolMap.set(l.source + '->' + l.target, l.vol || 0);
    });

    for (let i = 0; i < activeCols.length - 1; i++) {
      for (let j = i + 1; j < activeCols.length; j++) {
        const leftCol = activeCols[i];
        const rightCol = activeCols[j];

        if (!leftCol.fixed || !rightCol.fixed) {
          if (j !== i + 1) continue;

          const leftNodes = this.nodes.filter(n => n.columnId === leftCol.id);
          const rightNodes = this.nodes.filter(n => n.columnId === rightCol.id);

          // Determine which column is dynamic to compute vol on-the-fly
          const dynCol = !leftCol.fixed ? leftCol : (!rightCol.fixed ? rightCol : null);
          const dynField = dynCol?.field || null;

          for (const leftNode of leftNodes) {
            for (const rightNode of rightNodes) {
              let vol = masterVolMap.get(leftNode.id + '->' + rightNode.id) || 0;

              // Compute volume from nodeAgg rows if a dynamic column is involved
              if (vol === 0 && dynField) {
                const fixedNode = !leftCol.fixed ? rightNode : leftNode;
                const dynNode = !leftCol.fixed ? leftNode : rightNode;
                const fixedEntry = this.nodeAgg.get(fixedNode.id);
                if (fixedEntry) {
                  if ((dynNode as any).isSuperCard) {
                    // Super card: total flow = sum of ALL rows with any value in the dynamic field
                    vol = fixedEntry.rows.reduce((sum: number, row: any) => {
                      const v = row[dynField];
                      return (v !== null && v !== undefined && v !== '') ? sum + (row.count || 0) : sum;
                    }, 0);
                  } else {
                    vol = fixedEntry.rows.reduce((sum: number, row: any) => {
                      return (row[dynField] === dynNode.title) ? sum + (row.count || 0) : sum;
                    }, 0);
                  }
                }
              }

              // Super card links are NOT bundled — they get normal opacity and spread ports
              const isSuperLink = (leftNode as any).isSuperCard || (rightNode as any).isSuperCard;

              visibleLinks.push({
                source: leftNode.id,
                target: rightNode.id,
                vol: vol,
                bundled: !isSuperLink,
                sourceNode: this.nodeMap.get(leftNode.id),
                targetNode: this.nodeMap.get(rightNode.id)
              });
            }
          }
        } else {
          const excludeColumnIds = new Set([leftCol.id, rightCol.id]);
          for (let k = i + 1; k < j; k++) {
            excludeColumnIds.add(activeCols[k].id);
          }
          const leftNodes = this.nodes.filter(n => n.columnId === leftCol.id);
          const rightNodeIds = new Set(
            this.nodes.filter(n => n.columnId === rightCol.id).map(n => n.id)
          );

          for (const leftNode of leftNodes) {
            const reachable = this.bfsToTargets(leftNode.id, rightNodeIds, excludeColumnIds);
            for (const targetId of reachable) {
              visibleLinks.push({
                source: leftNode.id,
                target: targetId,
                vol: masterVolMap.get(leftNode.id + '->' + targetId) || 0,
                sourceNode: this.nodeMap.get(leftNode.id),
                targetNode: this.nodeMap.get(targetId)
              });
            }
          }
        }
      }
    }
    return visibleLinks;
  }

  setDynamicMode(mode: 'multi' | 'super'): void {
    if (this.dynamicDisplayMode === mode) return;
    this.dynamicDisplayMode = mode;
    this.getChartData();
  }

  // ── Column add/remove ──

  addColumn(columnId: string, afterPosition?: number): void {
    this.dismissDropdown();
    const col = this.allColumns.find(c => c.id === columnId);
    if (!col || col.active) return;

    col.active = true;

    const active = this.allColumns.filter(c => c.active && c.id !== col.id)
      .sort((a, b) => a.position - b.position);

    // Insert right after the column whose "+" was clicked (afterPosition),
    // or fall back to canonicalOrder-based insertion if no source was given.
    let insertPos: number;
    if (afterPosition !== undefined) {
      insertPos = afterPosition + 1;
    } else {
      insertPos = 0;
      for (const c of active) {
        if (c.canonicalOrder < col.canonicalOrder) {
          insertPos = c.position + 1;
        }
      }
    }

    active.filter(c => c.position >= insertPos).forEach(c => c.position += 1);
    col.position = insertPos;

    this.recalculatePositions();
    this.recalculateOptionalVolumes();
    this.rerender(true);
  }

  removeColumn(columnId: string): void {
    const col = this.allColumns.find(c => c.id === columnId);
    if (!col || col.fixed || !col.active) return;

    col.active = false;
    col.position = -1;
    this.recalculatePositions();
    this.recalculateOptionalVolumes();
    this.rerender(true);
  }

  private recalculateOptionalVolumes(): void {
    const activeCols = this.getActiveColumns();
    const props = this.props;
    const columnColours = props.columnColours || {};
    const columnPalettes = props.columnPalettes || {};
    const colours = props.colours || {};
    const defaultColour = props.defaultColour || '#a0aec0';
    const colorMode = props.colorMode || 'palette';
    const breakdownDims = props.breakdownDimensions || [];
    const colIdToLabel = new Map(this.allColumns.map((c: any) => [c.id, c.label]));

    // For each active dynamic column, recompute nodes from left neighbor's rows
    const dynamicCols = activeCols.filter((c: any) => !c.fixed);

    for (const dynCol of dynamicCols) {
      const dynField = dynCol.field;
      if (!dynField) continue;

      // Find the column immediately to the left
      const dynIdx = activeCols.indexOf(dynCol);
      const leftCol = dynIdx > 0 ? activeCols[dynIdx - 1] : null;

      // Collect rows from the left neighbor's nodes
      let sourceRows: any[] = [];
      if (leftCol) {
        this.nodeAgg.forEach((entry) => {
          if (entry.columnId === leftCol.id) {
            sourceRows = sourceRows.concat(entry.rows);
          }
        });
      }

      // Remove old dynamic nodes for this column
      this.nodes = this.nodes.filter(n => n.columnId !== dynCol.id);
      // Remove old nodeAgg entries for this column
      const keysToDelete: string[] = [];
      this.nodeAgg.forEach((_, key) => {
        if (key.startsWith(dynCol.id + '__')) keysToDelete.push(key);
      });
      keysToDelete.forEach(k => this.nodeAgg.delete(k));
      // Remove old breakdown entries for this column's nodes
      keysToDelete.forEach(k => delete this.breakdownData[k]);

      // Group source rows by the dynamic field
      const countMap = new Map<string, { vol: number, rows: any[] }>();
      sourceRows.forEach((row: any) => {
        const value = row[dynField];
        if (value === null || value === undefined || value === '') return;
        if (!countMap.has(value)) countMap.set(value, { vol: 0, rows: [] });
        const entry = countMap.get(value)!;
        entry.vol += (row.count || 0);
        entry.rows.push(row);
      });

      const colLabel = colIdToLabel.get(dynCol.id) || dynCol.label || dynCol.id;
      const baseColor = columnColours[colLabel] || columnColours[dynCol.id] || defaultColour;
      const palette = columnPalettes[colLabel] || columnPalettes[dynCol.id] || [baseColor];

      if (this.dynamicDisplayMode === 'super') {
        // Super card mode: ONE node with aggregate volume
        let totalVol = 0;
        const allRows: any[] = [];
        countMap.forEach((data) => { totalVol += data.vol; allRows.push(...data.rows); });

        const superNodeId = dynCol.id + '__super';
        this.nodes.push({
          id: superNodeId,
          title: dynCol.label || colLabel,
          vol: totalVol,
          pct: 100,
          columnId: dynCol.id,
          color: baseColor,
          y: this.chartHeight / 2,
          isSuperCard: true,
          field: dynCol.field,
        });

        const superEntry: any = { title: dynCol.label || colLabel, vol: totalVol, columnId: dynCol.id, stageSeq: 0, rows: allRows, isSuperCard: true, field: dynCol.field };
        this.nodeAgg.set(superNodeId, superEntry);

        // Breakdown = per-value list (what would have been individual cards)
        const dimLabel = breakdownDims.find((bd: any) => bd.field === dynCol.field)?.label || colLabel;
        const valueSorted = Array.from(countMap.entries()).sort((a, b) => b[1].vol - a[1].vol);
        const superBreakdown = valueSorted.map(([label, data]) => ({
          label,
          count: data.vol,
          pct: totalVol > 0 ? Math.round((data.vol / totalVol) * 100) : 0,
          color: defaultColour,
        }));
        this.breakdownData[superNodeId] = { [dimLabel]: superBreakdown };
      } else {
        // Multi-card mode: individual nodes, top 6 by volume
        const entries = Array.from(countMap.entries()).sort((a, b) => b[1].vol - a[1].vol).slice(0, 6);
        const columnTotal = entries.reduce((sum, e) => sum + e[1].vol, 0) || 1;
        const spacing = this.chartHeight / (entries.length + 1);

        entries.forEach(([value, data], idx) => {
          const nodeId = dynCol.id + '__' + this.slugify(value);

          let cardColor: string;
          if (colorMode === 'classic') {
            cardColor = colours[value] || defaultColour;
          } else if (colorMode === 'palette') {
            cardColor = idx < palette.length ? palette[idx] : palette[palette.length - 1];
          } else {
            cardColor = entries.length <= 1 ? baseColor : this.desaturateColor(baseColor, idx, entries.length);
          }

          this.nodes.push({
            id: nodeId,
            title: value,
            vol: data.vol,
            pct: columnTotal > 0 ? Math.round((data.vol / columnTotal) * 1000) / 10 : 0,
            columnId: dynCol.id,
            color: cardColor,
            y: spacing * (idx + 1),
          });

          this.nodeAgg.set(nodeId, { title: value, vol: data.vol, columnId: dynCol.id, stageSeq: idx, rows: data.rows });

          const nodeBreakdowns: any = {};
          breakdownDims.forEach((dim: any) => {
            const dimCountMap: any = {};
            let total = 0;
            data.rows.forEach((row: any) => {
              const v = row[dim.field];
              if (v === null || v === undefined || v === '') return;
              const rowCount = row.count || 1;
              if (!dimCountMap[v]) dimCountMap[v] = 0;
              dimCountMap[v] += rowCount;
              total += rowCount;
            });
            const dimBase = columnColours[colLabel] || defaultColour;
            const dimPalette = columnPalettes[colLabel] || [dimBase];
            const sorted = Object.entries(dimCountMap).sort((a: any, b: any) => b[1] - a[1]);
            nodeBreakdowns[dim.label] = sorted.map(([label, count]: any, i: number) => {
              let itemColor: string;
              if (colorMode === 'classic') {
                itemColor = colours[label] || defaultColour;
              } else if (colorMode === 'palette') {
                itemColor = i < dimPalette.length ? dimPalette[i] : dimPalette[dimPalette.length - 1];
              } else {
                itemColor = sorted.length <= 1 ? dimBase : this.desaturateColor(dimBase, i, sorted.length);
              }
              return { label, count, pct: total > 0 ? Math.round((count / total) * 100) : 0, color: itemColor };
            });
          });
          this.breakdownData[nodeId] = nodeBreakdowns;
        });
      }
    }
  }

  // ── Card interaction helpers ──

  private bringCardToFront(cardSelection: any): void {
    this.zCounter++;
    cardSelection.style('z-index', this.zCounter);
  }

  private dismissDropdown(): void {
    if (this.dropdownEl) {
      this.dropdownEl.remove();
      this.dropdownEl = null;
    }
  }

  private dismissGroupByPopup(): void {
    if (this.groupByPopupEl) {
      this.groupByPopupEl.remove();
      this.groupByPopupEl = null;
      d3.select('body').on('click.groupby-popup', null);
    }
  }

  private dismissLinkTooltip(): void {
    if (this.linkTooltipEl) {
      this.linkTooltipEl.remove();
      this.linkTooltipEl = null;
    }
  }


  private updateBreakdownRows(cardId: string, renderRow: (item: any, bi: number, d: any, rowH: number) => string): void {
    const cardNode = this.cardsContainer.selectAll('.funnel-card')
      .filter((fd: any) => fd.id === cardId).node() as HTMLElement;
    if (!cardNode) return;
    const scrollEl = cardNode.querySelector('.breakdown-scroll') as HTMLElement;
    if (!scrollEl) return;
    const d = d3.select(cardNode).datum() as any;
    const activeFilter = this.expandedCards.get(d.id);
    if (!activeFilter) return;
    const allItems = (this.breakdownData[d.id] || {})[activeFilter] || [];
    const searchTerm = this.cardSearchTerms.get(d.id) || '';
    const filteredItems = searchTerm
      ? allItems.filter((item: any) => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : allItems;

    const ROW_H = 28;
    const VIRT_BUFFER = 10;
    const endIdx = Math.min(filteredItems.length, VIRT_BUFFER * 2);

    // Update spacers
    const topSpacer = scrollEl.querySelector('.breakdown-spacer-top') as HTMLElement;
    const bottomSpacer = scrollEl.querySelector('.breakdown-spacer-bottom') as HTMLElement;
    if (topSpacer) topSpacer.style.height = '0px';
    if (bottomSpacer) bottomSpacer.style.height = `${Math.max(0, (filteredItems.length - endIdx) * ROW_H)}px`;

    // Remove existing rows
    scrollEl.querySelectorAll('.breakdown-row').forEach(r => r.remove());

    // Insert new filtered rows after top spacer
    let rowsHtml = '';
    for (let bi = 0; bi < endIdx; bi++) {
      rowsHtml += renderRow(filteredItems[bi], bi, d, ROW_H);
    }
    if (topSpacer) {
      topSpacer.insertAdjacentHTML('afterend', rowsHtml);
    }

    // Update total count on scroll container
    scrollEl.setAttribute('data-total', String(filteredItems.length));
    // Reset scroll position
    scrollEl.scrollTop = 0;
  }

  private applyCrossFilter(): void {
    const filterValue = this.crossFilterHover || this.crossFilterValue;
    const filterCardId = this.crossFilterCardId;
    const filterField = this.crossFilterField;

    if (!filterValue || !filterCardId || !filterField) {
      this.linkGroup?.selectAll('.linkPath')
        .transition().duration(150)
        .style('opacity', (d: any) => (d.bundled && !d._split) ? 0.15 : 0.6)
        .style('stroke-width', 1.5);
      this.crossFilterVolMap = null;
      return;
    }

    const filteredVolMap = new Map<string, number>();

    this.linkGroup?.selectAll('.linkPath').each((d: any) => {
      const linkKey = `${d.source}->${d.target}`;
      const touchesCard = (d.source === filterCardId || d.target === filterCardId);
      if (!touchesCard) return;

      const sourceNode = this.nodeMap.get(d.source);
      const targetNode = this.nodeMap.get(d.target);
      if (!sourceNode || !targetNode) return;

      let filteredVol = 0;

      // Super card link: the super card IS the filtered card. Filter the
      // OTHER (fixed) node's rows by the crossFilterField directly.
      if ((sourceNode as any).isSuperCard || (targetNode as any).isSuperCard) {
        const fixedNodeId = (sourceNode as any).isSuperCard ? d.target : d.source;
        const fixedEntry = this.nodeAgg.get(fixedNodeId);
        if (fixedEntry) {
          fixedEntry.rows.forEach((row: any) => {
            if (row[filterField] === filterValue) {
              filteredVol += (row.count || 0);
            }
          });
        }
      } else {
        // Regular fixed-to-fixed link: use parent_node_name matching
        const targetEntry = this.nodeAgg.get(d.target);
        if (targetEntry) {
          targetEntry.rows.forEach((row: any) => {
            if (row.parent_node_name === sourceNode.title && row[filterField] === filterValue) {
              filteredVol += (row.count || 0);
            }
          });
        }

        // Fallback for bundled dynamic links
        if (filteredVol === 0 && d.bundled) {
          const sourceEntry = this.nodeAgg.get(d.source);
          if (sourceEntry) {
            const dynCol = this.allColumns.find((c: any) => c.id === targetNode?.columnId);
            const dynField = dynCol?.field;
            if (dynField) {
              sourceEntry.rows.forEach((row: any) => {
                if (row[dynField] === targetNode?.title && row[filterField] === filterValue) {
                  filteredVol += (row.count || 0);
                }
              });
            }
          }
        }
      }

      filteredVolMap.set(linkKey, filteredVol);
    });

    this.linkGroup?.selectAll('.linkPath')
      .transition().duration(150)
      .style('opacity', (d: any) => {
        const linkKey = `${d.source}->${d.target}`;
        const touchesCard = (d.source === filterCardId || d.target === filterCardId);
        if (!touchesCard) return 0.08;
        const vol = filteredVolMap.get(linkKey) || 0;
        return vol > 0 ? 0.9 : 0.05;
      })
      .style('stroke-width', (d: any) => {
        const linkKey = `${d.source}->${d.target}`;
        const vol = filteredVolMap.get(linkKey) || 0;
        return vol > 0 ? 2.5 : 1.5;
      });

    this.crossFilterVolMap = filteredVolMap;
  }

  private toggleCardExpansion(nodeId: string): void {
    const wasExpanded = this.expandedCards.has(nodeId);

    if (wasExpanded) {
      this.expandedCards.delete(nodeId);
      this.openDropdowns.delete(nodeId);
      this.cardSearchTerms.delete(nodeId);
      if (this.crossFilterCardId === nodeId) {
        this.crossFilterCardId = null;
        this.crossFilterField = null;
        this.crossFilterValue = null;
        this.crossFilterHover = null;
        this.crossFilterVolMap = null;
      }
    } else {
      this.expandedCards.set(nodeId, 'Patient Type');
    }

    const cardSel = this.cardsContainer.selectAll('.funnel-card')
      .filter((d: any) => d.id === nodeId);
    const cardNode = cardSel.node() as HTMLElement;
    if (!cardNode) return;

    this.bringCardToFront(cardSel);
    const d = d3.select(cardNode).datum() as any;
    const isExpanded = this.expandedCards.has(nodeId);
    const expandedCardWidth = 250;

    // Update card width and border without rebuilding HTML
    const offset = isExpanded ? (expandedCardWidth - this.cardWidth) / 2 : 0;
    const newWidth = isExpanded ? expandedCardWidth : this.cardWidth;
    const newLeft = d.x - offset;
    d._renderX = newLeft;
    d._renderWidth = newWidth;
    d3.select(cardNode)
      .style('width', `${newWidth}px`)
      .style('left', `${newLeft}px`);

    const cardInner = cardNode.querySelector('.card-inner') as HTMLElement;
    if (cardInner) {
      cardInner.style.borderColor = isExpanded ? d.color + '40' : '#e2e8f0';
    }

    if (isExpanded) {
      // Hide chevron
      const chevron = cardNode.querySelector('.card-chevron') as HTMLElement;
      if (chevron) chevron.style.display = 'none';

      // Build and insert expanded section (only if not already present)
      let expanded = cardNode.querySelector('.card-expanded-section');
      if (!expanded) {
        // Use renderCards to build this one card (it will create the expanded section)
        this.renderCards(undefined, nodeId);
      }
    } else {
      // Animate collapse then remove expanded section
      const expanded = cardNode.querySelector('.card-expanded-section') as HTMLElement;
      if (expanded) {
        expanded.style.animation = 'cardCollapseOut 0.2s ease-in forwards';
        expanded.style.overflow = 'hidden';
        setTimeout(() => expanded.remove(), 200);
      }

      // Show chevron
      const chevron = cardNode.querySelector('.card-chevron') as HTMLElement;
      if (chevron) {
        chevron.style.display = '';
      } else {
        // Re-add chevron if it was removed (first-time collapse after renderCards built without it)
        const progressRow = cardNode.querySelector('.card-progress-row');
        if (progressRow) {
          progressRow.insertAdjacentHTML('beforeend', `
            <div class="card-chevron">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 4.5L6 7.5L9 4.5" stroke="#8A98AB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>`);
          // Attach click handler
          const newChevron = progressRow.querySelector('.card-chevron');
          if (newChevron) {
            newChevron.addEventListener('click', (event: Event) => {
              event.stopPropagation();
              this.toggleCardExpansion(nodeId);
            });
          }
        }
      }
    }

    this.updateLinks();
    this.applyCrossFilter();
  }

  private selectFilter(nodeId: string, filter: string): void {
    this.expandedCards.set(nodeId, filter);
    this.openDropdowns.delete(nodeId);
    this.cardSearchTerms.delete(nodeId);
    if (this.crossFilterCardId === nodeId) {
      this.crossFilterCardId = null;
      this.crossFilterField = null;
      this.crossFilterValue = null;
      this.crossFilterHover = null;
      this.crossFilterVolMap = null;
    }

    // Direct DOM update: close dropdown, update label, rebuild breakdown list
    const cardNode = this.cardsContainer.selectAll('.funnel-card')
      .filter((d: any) => d.id === nodeId).node() as HTMLElement;
    if (cardNode) {
      // Close dropdown
      const dropdown = cardNode.querySelector('.filter-dropdown-options');
      if (dropdown) dropdown.remove();
      // Update the filter label text
      const label = cardNode.querySelector('.filter-trigger-label');
      if (label) {
        // Preserve the caret SVG inside the label
        const caret = label.querySelector('svg');
        label.textContent = filter;
        if (caret) {
          caret.style.transform = 'rotate(0deg)';
          label.appendChild(caret);
        }
      }
      // Update the search placeholder
      const searchInput = cardNode.querySelector('.breakdown-search-input') as HTMLInputElement;
      if (searchInput) {
        searchInput.placeholder = `Search`;
        searchInput.value = '';
      }
    }

    // Rebuild breakdown list for the new filter (uses renderCards for this one card)
    this.renderCards(undefined, nodeId);
    this.updateLinks();
    this.applyCrossFilter();
  }

  private toggleFilterDropdown(nodeId: string): void {
    const isOpen = this.openDropdowns.has(nodeId);

    if (isOpen) {
      this.openDropdowns.delete(nodeId);
    } else {
      this.openDropdowns.add(nodeId);
    }

    const cardNode = this.cardsContainer.selectAll('.funnel-card')
      .filter((d: any) => d.id === nodeId).node() as HTMLElement;
    if (!cardNode) return;

    const nowOpen = this.openDropdowns.has(nodeId);
    const existing = cardNode.querySelector('.filter-dropdown-options');

    if (nowOpen && !existing) {
      // Build and insert dropdown options
      const activeFilter = this.expandedCards.get(nodeId);
      const filterOptions = this.props.filterOptions || [];
      let optionsHtml = '<div class="filter-dropdown-options">';
      for (const opt of filterOptions) {
        const isActive = opt === activeFilter;
        optionsHtml += `
          <div class="filter-option" data-filter="${opt}" style="font-weight: ${isActive ? '600' : '500'}; color: ${isActive ? '#1363DF' : '#737D88'}; background: ${isActive ? '#eff6ff' : 'transparent'};">
              <span>${opt}</span>
              ${isActive ? '<span class="filter-option-check">&#10003;</span>' : ''}
          </div>`;
      }
      optionsHtml += '</div>';

      const filterRow = cardNode.querySelector('.filter-search-row');
      if (filterRow) {
        filterRow.insertAdjacentHTML('afterend', optionsHtml);
        // Attach click handlers on the new options
        const optionEls = cardNode.querySelectorAll('.filter-option');
        optionEls.forEach((el: Element) => {
          el.addEventListener('click', (event: Event) => {
            event.stopPropagation();
            const filter = (el as HTMLElement).getAttribute('data-filter')!;
            this.selectFilter(nodeId, filter);
          });
          el.addEventListener('mouseover', () => {
            if (!(el as HTMLElement).style.background.includes('eff6ff')) {
              (el as HTMLElement).style.background = '#f1f5f9';
            }
          });
          el.addEventListener('mouseout', () => {
            if (!(el as HTMLElement).style.background.includes('eff6ff')) {
              (el as HTMLElement).style.background = 'transparent';
            }
          });
        });
      }
    } else if (!nowOpen && existing) {
      existing.remove();
    }

    // Rotate the caret
    const caretSvg = cardNode.querySelector('.filter-trigger-label svg');
    if (caretSvg) {
      (caretSvg as HTMLElement).style.transform = `rotate(${nowOpen ? '180' : '0'}deg)`;
    }
  }

  // ── Popups & Dropdowns ──

  private showGroupByPopup(event: MouseEvent, node?: any, targetEl?: HTMLElement): void {
    this.dismissGroupByPopup();

    const options = this.props.filterOptions || ['Patient Type', 'Payer Type', 'Groups', 'Practices', 'Locations'];
    const el = targetEl || (event.currentTarget as HTMLElement);
    const rect = el.getBoundingClientRect();
    const dotColor = node ? node.color : '#737D88';

    this.groupByPopupEl = d3.select('body').append('div')
      .attr('class', 'groupby-popup')
      .style('position', 'fixed')
      .style('z-index', '10000')
      .style('visibility', 'hidden')
      .style('background', '#0f172a')
      .style('color', '#f8fafc')
      .style('border-radius', '10px')
      .style('padding', '0')
      .style('font-family', "'Poppins', sans-serif")
      .style('font-size', '12px')
      .style('box-shadow', '0 8px 24px rgba(0,0,0,0.35)')
      .style('min-width', '170px')
      .style('max-width', '300px')
      .style('overflow', 'hidden');

    this.groupByPopupEl.append('div')
      .style('background', '#101D42')
      .style('padding', '10px 16px')
      .style('border-radius', '10px 10px 0 0')
      .append('div')
      .style('font-weight', '700')
      .style('font-size', '11px')
      .style('text-transform', 'uppercase')
      .style('letter-spacing', '0.5px')
      .style('color', '#f1f5f9')
      .text('Group by');

    const body = this.groupByPopupEl.append('div')
      .style('padding', '6px 0');

    options.forEach((option: string) => {
      body.append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('padding', '8px 16px')
        .style('cursor', 'pointer')
        .style('color', '#8A98AB')
        .style('font-size', '12px')
        .style('font-weight', '500')
        .style('transition', 'background 0.15s, color 0.15s')
        .html(`<span style="width:8px;height:8px;border-radius:50%;background:${dotColor};margin-right:10px;flex-shrink:0;"></span><span>${option}</span>`)
        .on('mouseover', (_event: any) => {
          d3.select(_event.currentTarget).style('background', '#1e293b').style('color', '#f1f5f9');
        })
        .on('mouseout', (_event: any) => {
          d3.select(_event.currentTarget).style('background', 'transparent').style('color', '#8A98AB');
        })
        .on('click', (_event: any) => {
          _event.stopPropagation();
          this.dismissGroupByPopup();
        });
    });

    this.groupByPopupEl.style('visibility', 'visible').style('opacity', '1');
    const popupRect = (this.groupByPopupEl.node() as HTMLElement).getBoundingClientRect();
    const GAP = 8;
    let top = rect.top - popupRect.height - GAP;
    if (top < 4) {
      top = rect.bottom + GAP;
    }
    let left = rect.left + rect.width / 2 - popupRect.width / 2;
    left = Math.max(4, Math.min(left, window.innerWidth - popupRect.width - 4));

    this.groupByPopupEl
      .style('left', `${left}px`)
      .style('top', `${top}px`);

    setTimeout(() => {
      d3.select('body').on('click.groupby-popup', () => {
        this.dismissGroupByPopup();
      });
    }, 10);
  }

  private showDropdown(event: MouseEvent, sourceCol?: any): void {
    this.dismissDropdown();
    const inactive = this.getInactiveOptionalColumns();
    if (inactive.length === 0) return;

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

    this.dropdownEl = d3.select('body').append('div')
      .attr('class', 'funnel-dropdown')
      .style('position', 'fixed')
      .style('left', `${rect.left}px`)
      .style('top', `${rect.bottom + 4}px`)
      .style('z-index', '9999')
      .style('background', '#ffffff')
      .style('border', '1px solid #e2e8f0')
      .style('border-radius', '8px')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.15)')
      .style('padding', '4px 0')
      .style('min-width', '160px')
      .style('max-height', '300px')
      .style('overflow-y', 'auto')
      .style('overflow-x', 'hidden')
      .style('font-family', "'Poppins', sans-serif");

    inactive.forEach(col => {
      this.dropdownEl.append('div')
        .attr('class', 'funnel-dropdown-item')
        .text(col.label)
        .style('padding', '9px 16px')
        .style('font-size', '13px')
        .style('font-weight', '500')
        .style('color', '#101D42')
        .style('cursor', 'pointer')
        .on('mouseover', (_event: any) => {
          d3.select(_event.currentTarget).style('background', '#f1f5f9').style('color', '#1363DF');
        })
        .on('mouseout', (_event: any) => {
          d3.select(_event.currentTarget).style('background', 'transparent').style('color', '#101D42');
        })
        .on('click', () => {
          this.addColumn(col.id, sourceCol?.position);
        });
    });

    // Bounds correction: flip above the trigger if the dropdown would overflow
    // the viewport bottom; clamp horizontally to keep it inside the viewport.
    const dropdownNode = this.dropdownEl.node() as HTMLElement;
    const dropdownRect = dropdownNode.getBoundingClientRect();
    const GAP = 4;

    let top = rect.bottom + GAP;
    if (top + dropdownRect.height > window.innerHeight - GAP) {
      const flippedTop = rect.top - dropdownRect.height - GAP;
      top = flippedTop >= GAP
        ? flippedTop
        : Math.max(GAP, window.innerHeight - dropdownRect.height - GAP);
    }

    let left = rect.left;
    left = Math.max(GAP, Math.min(left, window.innerWidth - dropdownRect.width - GAP));

    this.dropdownEl
      .style('top', `${top}px`)
      .style('left', `${left}px`);

    setTimeout(() => {
      d3.select('body').on('click.funnel-dropdown', () => {
        this.dismissDropdown();
        d3.select('body').on('click.funnel-dropdown', null);
      });
    }, 10);
  }

  // ── Rerender (animated transitions) ──

  private rerender(animate: boolean): void {
    if (!this.wrapper) return;

    // Rebuild nodeMap after dynamic nodes may have changed
    this.nodeMap = new Map(this.nodes.map(n => [n.id, n]));

    const containerWidth = this.calculateAvailableWidth();
    const activeCols = this.getActiveColumns();
    // Minimum colWidth = cardWidth + 100, so the gap between adjacent column
    // cards is at least 100px. When the container is too narrow to fit all
    // columns at this minimum, the funnel-wrapper exceeds the container width
    // and the .chart-scroll-wrapper (overflow-x: auto in SCSS) shows a scrollbar.
    this.colWidth = Math.max(this.cardWidth + 100, containerWidth / activeCols.length);
    this.recalculatePositions();

    // Set x positions on dynamic nodes that were just created
    const colMap = new Map(this.allColumns.map((c: any) => [c.id, c]));
    this.nodes.forEach(n => {
      const col = colMap.get(n.columnId);
      if (col && col.active) n.x = col.x;
    });

    const neededWidth = (activeCols.length - 1) * this.colWidth + this.cardWidth;
    const width = Math.max(containerWidth, neededWidth);
    const t = animate ? d3.transition().duration(300) : null;

    if (t) {
      this.wrapper.transition(t).style('width', `${width}px`);
    } else {
      this.wrapper.style('width', `${width}px`);
    }

    this.renderLines(activeCols, t);
    this.renderGrips(activeCols, t);
    this.renderCards(t);
    this.updateLinks(t);
  }

  // ── Render: column lines ──

  private renderLines(activeCols: any[], transition?: any): void {
    const lines = this.lineGroup.selectAll('.col-line')
      .data(activeCols, (d: any) => d.id);

    lines.exit().remove();

    const enter = lines.enter().append('line')
      .attr('class', 'col-line')
      .style('stroke', '#E8EAEE')
      .style('stroke-dasharray', '4 4')
      .style('stroke-width', '1');

    const merged = enter.merge(lines);

    if (transition) {
      merged.transition(transition)
        .attr('x1', (d: any) => d.x + (this.cardWidth / 2))
        .attr('y1', 0)
        .attr('x2', (d: any) => d.x + (this.cardWidth / 2))
        .attr('y2', this.chartHeight);
    } else {
      merged
        .attr('x1', (d: any) => d.x + (this.cardWidth / 2))
        .attr('y1', 0)
        .attr('x2', (d: any) => d.x + (this.cardWidth / 2))
        .attr('y2', this.chartHeight);
    }
  }

  // ── Render: column grips (labels + drag handles) ──

  private renderGrips(activeCols: any[], _transition?: any): void {
    const self = this;
    const hasInactive = this.getInactiveOptionalColumns().length > 0;

    this.gripsGroup.selectAll('*').remove();

    const grips = this.gripsGroup.selectAll('.col-grip')
      .data(activeCols, (d: any) => d.id)
      .join('div')
      .attr('class', (d: any) => `col-grip ${d.fixed ? 'col-grip-fixed' : 'col-grip-optional'}`)
      .style('position', 'absolute')
      .style('left', (d: any) => `${d.x + (this.cardWidth / 2) - 70}px`)
      .style('top', `${this.chartHeight + 20}px`)
      .style('width', '140px')
      .style('text-align', 'center')
      .style('font-size', '11px')
      .style('color', '#737D88')
      .style('font-weight', '600')
      .style('display', 'flex')
      .style('flex-direction', 'column')
      .style('align-items', 'center')
      .style('gap', '6px')
      .style('user-select', 'none')
      .style('font-family', 'Poppins');

    grips.append('div')
      .attr('class', 'plus-btn-row')
      .style('display', hasInactive ? 'flex' : 'none')
      .style('justify-content', 'center')
      .append('div')
      .attr('class', 'plus-btn')
      .style('width', '20px')
      .style('height', '20px')
      .style('border-radius', '50%')
      .style('background', '#edf2f7')
      .style('border', '1px solid #e2e8f0')
      .style('color', '#718096')
      .style('font-size', '14px')
      .style('font-weight', '700')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('cursor', 'pointer')
      .style('line-height', '1')
      .text('+')
      .on('click', function(event: MouseEvent, d: any) {
        event.stopPropagation();
        self.showDropdown(event, d);
      })
      .on('mouseover', (event: any) => {
        d3.select(event.currentTarget).style('background', '#1363DF').style('color', 'white').style('border-color', '#1363DF');
      })
      .on('mouseout', (event: any) => {
        d3.select(event.currentTarget).style('background', '#edf2f7').style('color', '#718096').style('border-color', '#e2e8f0');
      });

    const labelRow = grips.append('div')
      .attr('class', 'grip-label-row')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('gap', '5px')
      .style('padding', '4px 8px')
      .style('border-radius', '4px')
      .style('border', '1px solid transparent')
      .style('cursor', (d: any) => d.fixed ? 'default' : 'grab');

    labelRow.filter((d: any) => d.fixed)
      .append('span')
      .style('display', 'inline-flex')
      .style('align-items', 'center')
      .style('pointer-events', 'none')
      .style('flex-shrink', '0')
      .html('<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#8A98AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>');

    labelRow.filter((d: any) => !d.fixed)
      .append('span')
      .style('display', 'inline-flex')
      .style('align-items', 'center')
      .style('pointer-events', 'none')
      .style('flex-shrink', '0')
      .html('<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12"><circle cx="3.5" cy="2.5" r="1.1" fill="#8A98AB"/><circle cx="8.5" cy="2.5" r="1.1" fill="#8A98AB"/><circle cx="3.5" cy="6" r="1.1" fill="#8A98AB"/><circle cx="8.5" cy="6" r="1.1" fill="#8A98AB"/><circle cx="3.5" cy="9.5" r="1.1" fill="#8A98AB"/><circle cx="8.5" cy="9.5" r="1.1" fill="#8A98AB"/></svg>');

    labelRow.append('span')
      .style('pointer-events', 'none')
      .style('white-space', 'nowrap')
      .text((d: any) => d.label);

    labelRow.filter((d: any) => !d.fixed)
      .append('span')
      .attr('class', 'remove-col-btn')
      .style('display', 'inline-flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('width', '16px')
      .style('height', '16px')
      .style('border-radius', '50%')
      .style('background', '#fed7d7')
      .style('color', '#e53e3e')
      .style('font-size', '10px')
      .style('font-weight', '700')
      .style('cursor', 'pointer')
      .style('flex-shrink', '0')
      .style('pointer-events', 'auto')
      .text('\u2715')
      .on('click', function(event: any, d: any) {
        event.stopPropagation();
        self.removeColumn(d.id);
      })
      .on('mouseover', (event: any) => {
        d3.select(event.currentTarget).style('background', '#e53e3e').style('color', 'white');
      })
      .on('mouseout', (event: any) => {
        d3.select(event.currentTarget).style('background', '#fed7d7').style('color', '#e53e3e');
      });

    labelRow.filter((d: any) => !d.fixed)
      .on('mouseover', (event: any) => {
        d3.select(event.currentTarget).style('background', '#f7fafc').style('border-color', '#e2e8f0');
      })
      .on('mouseout', (event: any) => {
        d3.select(event.currentTarget).style('background', 'transparent').style('border-color', 'transparent');
      });

    grips.filter((d: any) => d.fixed)
      .on('dblclick', (event: any) => {
        event.stopPropagation();
        this.showGroupByPopup(event, undefined, event.currentTarget as HTMLElement);
      });

    const optionalGrips = this.gripsGroup.selectAll('.col-grip-optional');
    (optionalGrips as d3.Selection<HTMLDivElement, any, any, any>).call(d3.drag<HTMLDivElement, any>()
      .filter((event: any) => !event.ctrlKey && !event.button && !event.target.closest('.remove-col-btn') && !event.target.closest('.plus-btn'))
      .on('start', function() {
        d3.select(this as any).select('.grip-label-row').style('cursor', 'grabbing');
      })
      .on('drag', (event: any, c: any) => {
        c.x += event.dx;

        // Move the dragged column's grip and line
        this.gripsGroup.selectAll('.col-grip')
          .filter((d: any) => d.id === c.id)
          .style('left', `${c.x + (this.cardWidth / 2) - 70}px`);

        this.lineGroup.selectAll('.col-line')
          .filter((d: any) => d.id === c.id)
          .attr('x1', c.x + (this.cardWidth / 2))
          .attr('x2', c.x + (this.cardWidth / 2));

        this.nodes.filter(n => n.columnId === c.id).forEach(n => n.x = c.x);
        this.cardsContainer.selectAll('.funnel-card')
          .filter((d: any) => d.columnId === c.id)
          .style('left', (d: any) => `${d.x}px`);

        // Live reorder: other columns shift to make space
        const dragCenterX = c.x + (this.cardWidth / 2);
        const others = this.getActiveColumns().filter(col => col.id !== c.id);
        others.sort((a, b) => a.position - b.position);

        let newPos = 0;
        for (const col of others) {
          const colCenter = col.x + (this.cardWidth / 2);
          if (colCenter < dragCenterX) newPos = col.position + 1;
        }
        newPos = Math.max(0, Math.min(newPos, others.length));

        // Reassign positions for non-dragged columns
        let pos = 0;
        others.forEach(col => {
          if (pos === newPos) pos++;
          col.position = pos;
          col.x = pos * this.colWidth;
          pos++;
        });
        c.position = newPos;

        // Animate other columns to their new positions
        const t = d3.transition().duration(150);

        this.gripsGroup.selectAll('.col-grip')
          .filter((d: any) => d.id !== c.id)
          .transition(t)
          .style('left', (d: any) => `${d.x + (this.cardWidth / 2) - 70}px`);

        this.lineGroup.selectAll('.col-line')
          .filter((d: any) => d.id !== c.id)
          .transition(t)
          .attr('x1', (d: any) => d.x + (this.cardWidth / 2))
          .attr('x2', (d: any) => d.x + (this.cardWidth / 2));

        others.forEach(col => {
          this.nodes.filter(n => n.columnId === col.id).forEach(n => n.x = col.x);
        });
        this.cardsContainer.selectAll('.funnel-card')
          .filter((d: any) => d.columnId !== c.id)
          .transition(t)
          .style('left', (d: any) => `${d.x}px`);

        this.updateLinks();
      })
      .on('end', (_event: any, c: any) => {
        const activeCols = this.getActiveColumns();

        let newPos = 0;
        for (const col of activeCols) {
          if (col.id === c.id) continue;
          if ((col.x || 0) < c.x) {
            newPos = col.position + 1;
          }
        }
        newPos = Math.max(0, Math.min(newPos, activeCols.length - 1));

        const otherActive = activeCols.filter(col => col.id !== c.id);
        otherActive.forEach((col, i) => col.position = i);
        otherActive.forEach(col => {
          if (col.position >= newPos) col.position += 1;
        });
        c.position = newPos;

        this.recalculatePositions();
        this.recalculateOptionalVolumes();
        this.rerender(true);
      })
    );
  }

  // ── Render: cards ──

  private renderCards(transition?: any, onlyCardId?: string): void {
    this.dismissLinkTooltip();
    const visibleNodes = this.getVisibleNodes();
    const self = this;
    const filterOptions = this.props.filterOptions || ['Patient Type', 'Payer Type', 'Groups', 'Practices', 'Locations'];

    const renderBreakdownRow = (item: any, bi: number, d: any, rowH: number): string => {
      const itemVol = item.count != null ? item.count : Math.round(d.vol * item.pct / 100);
      const isFilterActive = this.crossFilterValue === item.label && this.crossFilterCardId === d.id;
      return `
        <div class="breakdown-row ${isFilterActive ? 'breakdown-row--active' : ''}"
          data-label="${item.label}" data-index="${bi}"
          style="height:${rowH}px; box-sizing:border-box;">
          <div class="breakdown-row-header">
            <span class="truncated-label breakdown-label" style="color: ${item.color};">${item.label}</span>
            <div class="breakdown-vol-group">
              <span class="breakdown-vol">${itemVol.toLocaleString()}</span>
              <span class="breakdown-pct">${item.pct}%</span>
            </div>
          </div>
          <div class="breakdown-bar-track">
            <div class="breakdown-bar" style="width: ${item.pct}%; background: ${item.color};"></div>
          </div>
        </div>`;
    };

    const cards = this.cardsContainer.selectAll('.funnel-card')
      .data(visibleNodes, (d: any) => d.id);

    cards.exit()
      .transition().duration(200)
      .style('opacity', '0')
      .remove();

    const expandedCardWidth = 250;
    // Compute the rendered left position and width for each card.
    // Expanded/super cards are wider and need to be centered on the column.
    const getCardRenderProps = (d: any) => {
      const isWide = d.isSuperCard || this.expandedCards.has(d.id);
      const w = isWide ? expandedCardWidth : this.cardWidth;
      const offset = isWide ? (expandedCardWidth - this.cardWidth) / 2 : 0;
      const left = d.x - offset;
      return { w, left };
    };

    const enter = cards.enter().append('div')
      .attr('class', 'funnel-card')
      .style('position', 'absolute')
      .style('left', (d: any) => `${getCardRenderProps(d).left}px`)
      .style('top', (d: any) => `${d.y}px`)
      .style('width', (d: any) => `${getCardRenderProps(d).w}px`)
      .style('z-index', '10')
      .style('font-family', 'Poppins')
      .style('opacity', '0');

    enter.transition().duration(300).style('opacity', '1');

    const merged = enter.merge(cards);

    if (transition) {
      merged.transition(transition)
        .style('left', (d: any) => `${getCardRenderProps(d).left}px`)
        .style('top', (d: any) => `${d.y}px`)
        .style('width', (d: any) => `${getCardRenderProps(d).w}px`);
    } else {
      merged
        .style('left', (d: any) => `${getCardRenderProps(d).left}px`)
        .style('top', (d: any) => `${d.y}px`)
        .style('width', (d: any) => `${getCardRenderProps(d).w}px`);
    }

    merged.each((d: any, i: number, nodes: any) => {
      // Skip cards that haven't changed to avoid visual flash from HTML replacement
      if (onlyCardId && d.id !== onlyCardId) return;

      const cardEl = d3.select(nodes[i]);
      const isSuperCard = d.isSuperCard || false;

      // Store render position/width for link calculations
      const renderProps = getCardRenderProps(d);
      d._renderX = renderProps.left;
      d._renderWidth = renderProps.w;

      // Super cards are always expanded with a fixed filter matching their field
      if (isSuperCard && !this.expandedCards.has(d.id)) {
        const dimLabel = this.props.breakdownDimensions?.find((bd: any) => bd.field === d.field)?.label || d.title;
        this.expandedCards.set(d.id, dimLabel);
      }

      const isExpanded = this.expandedCards.has(d.id);
      const activeFilter = this.expandedCards.get(d.id);
      const isDropdownOpen = this.openDropdowns.has(d.id);
      const breakdownItems = activeFilter ? (this.breakdownData[d.id] || {})[activeFilter] || [] : [];

      let html = `
      <div class="card-inner" style="border-color: ${isExpanded ? d.color + '40' : '#e2e8f0'};">
          <div class="card-header-section">
              <div class="truncated-label card-title" style="color: ${d.color};">${d.title}</div>
              <div class="card-vol-row">
                  <span class="card-vol">${d.vol.toLocaleString()}</span>
                  <span class="card-pct">${d.pct}%</span>
              </div>
              <div class="card-progress-row">
                  <div class="card-progress-track">
                      <div class="card-progress-bar" style="width: ${d.pct}%; background: ${d.color};"></div>
                  </div>`;

      if (!isExpanded && !isSuperCard) {
        html += `
                  <div class="card-chevron">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="#8A98AB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                  </div>`;
      }

      html += `
              </div>
          </div>`;

      if (isExpanded) {
        html += `<div class="card-expanded-section">`;

        const searchTerm = this.cardSearchTerms.get(d.id) || '';

        // Filter row: GROUPS ▼ on left, Search 🔍 input on right (always visible)
        const caretSvg = `<svg width="10" height="10" viewBox="0 0 12 12" fill="none" style="transform: rotate(${isDropdownOpen ? '180' : '0'}deg); vertical-align: middle; margin-left: 2px;"><path d="M3 4.5L6 7.5L9 4.5" stroke="#8A98AB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        html += `
          <div class="filter-search-row">
              <span class="filter-trigger-label ${isSuperCard ? '' : 'filter-dropdown-trigger'}" style="flex-shrink: 0; cursor: ${isSuperCard ? 'default' : 'pointer'};">${activeFilter}${!isSuperCard ? caretSvg : ''}</span>
              <div class="breakdown-search-wrapper">
                <input class="breakdown-search-input" type="text" placeholder="Search" value="${searchTerm}" data-card-id="${d.id}" />
                <svg class="breakdown-search-icon-right" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8A98AB" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </div>
          </div>`;

        if (!isSuperCard && isDropdownOpen) {
          html += `<div class="filter-dropdown-options">`;
          for (const opt of filterOptions) {
            const isActive = opt === activeFilter;
            html += `
                <div class="filter-option" data-filter="${opt}" style="font-weight: ${isActive ? '600' : '500'}; color: ${isActive ? '#1363DF' : '#737D88'}; background: ${isActive ? '#eff6ff' : 'transparent'};">
                    <span>${opt}</span>
                    ${isActive ? '<span class="filter-option-check">&#10003;</span>' : ''}
                </div>`;
          }
          html += `</div>`;
        }

        if (breakdownItems.length > 0) {

          // Filter items by search term
          const filteredItems = searchTerm
            ? breakdownItems.filter((item: any) => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
            : breakdownItems;

          const ROW_H = 28;
          const VIRT_BUFFER = 10;
          const endIdx = Math.min(filteredItems.length, VIRT_BUFFER * 2);
          const bottomSpacerH = Math.max(0, (filteredItems.length - endIdx) * ROW_H);

          html += `<div class="breakdown-scroll" data-card-id="${d.id}" data-total="${filteredItems.length}">`;
          html += `<div class="breakdown-spacer-top" style="height:0px;"></div>`;
          for (let bi = 0; bi < endIdx; bi++) {
            html += renderBreakdownRow(filteredItems[bi], bi, d, ROW_H);
          }
          html += `<div class="breakdown-spacer-bottom" style="height:${bottomSpacerH}px;"></div>`;
          html += `</div>`;
        }

        if (!isSuperCard) {
          html += `
              <div class="card-collapse-btn">
                  <div class="collapse-icon">
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                          <path d="M3 7.5L6 4.5L9 7.5" stroke="#e53e3e" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                  </div>
              </div>`;
        }

        html += `</div>`;
      }

      html += `</div>`;
      cardEl.html(html);
    });

    merged.selectAll('.card-chevron')
      .on('click', (event: any) => {
        event.stopPropagation();
        const cardEl = (event.currentTarget as HTMLElement).closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        this.toggleCardExpansion(d.id);
      });

    merged.selectAll('.filter-dropdown-trigger')
      .on('click', (event: any) => {
        event.stopPropagation();
        const cardEl = (event.currentTarget as HTMLElement).closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        this.toggleFilterDropdown(d.id);
      });

    merged.selectAll('.filter-option')
      .on('click', (event: any) => {
        event.stopPropagation();
        const filter = (event.currentTarget as HTMLElement).getAttribute('data-filter')!;
        const cardEl = (event.currentTarget as HTMLElement).closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        this.selectFilter(d.id, filter);
      })
      .on('mouseover', (event: any) => {
        const el = event.currentTarget as HTMLElement;
        if (!el.style.background.includes('eff6ff')) {
          el.style.background = '#f1f5f9';
        }
      })
      .on('mouseout', (event: any) => {
        const el = event.currentTarget as HTMLElement;
        if (!el.style.background.includes('eff6ff')) {
          el.style.background = 'transparent';
        }
      });

    // Search input — filters the breakdown list in-place (no full card re-render)
    merged.selectAll('.breakdown-search-input')
      .on('input', (event: any) => {
        event.stopPropagation();
        const input = event.currentTarget as HTMLInputElement;
        const cardId = input.getAttribute('data-card-id');
        if (!cardId) return;
        const term = input.value.trim();
        if (term) {
          this.cardSearchTerms.set(cardId, term);
        } else {
          this.cardSearchTerms.delete(cardId);
        }
        // Update the breakdown rows in-place (no renderCards call)
        this.updateBreakdownRows(cardId, renderBreakdownRow);
      })
      .on('click', (event: any) => {
        event.stopPropagation();
      });

    merged.selectAll('.card-collapse-btn')
      .on('click', (event: any) => {
        event.stopPropagation();
        const cardEl = (event.currentTarget as HTMLElement).closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        this.toggleCardExpansion(d.id);
      })
      .on('mouseover', (event: any) => {
        (event.currentTarget as HTMLElement).style.background = '#f8fafc';
      })
      .on('mouseout', (event: any) => {
        (event.currentTarget as HTMLElement).style.background = 'transparent';
      });

    // Instant tooltip for truncated labels (card titles + breakdown items).
    // Only shows when text IS actually truncated (scrollWidth > offsetWidth).
    // Reuses the existing linkTooltipEl + dismissLinkTooltip() infrastructure.
    merged.selectAll('.truncated-label')
      .on('mouseenter', (event: any) => {
        const el = event.currentTarget as HTMLElement;
        if (el.scrollWidth <= el.offsetWidth) return;
        this.dismissLinkTooltip();
        const rect = el.getBoundingClientRect();
        this.linkTooltipEl = d3.select('body').append('div')
          .style('position', 'fixed')
          .style('z-index', '10001')
          .style('pointer-events', 'none')
          .style('background', '#0f172a')
          .style('color', '#f8fafc')
          .style('border-radius', '8px')
          .style('padding', '6px 12px')
          .style('font-family', "'Poppins', sans-serif")
          .style('font-size', '12px')
          .style('font-weight', '600')
          .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
          .style('white-space', 'nowrap')
          .style('left', `${rect.left}px`)
          .style('top', `${rect.top - 32}px`)
          .text(el.textContent || '');
      })
      .on('mouseleave', () => {
        this.dismissLinkTooltip();
      });

    // Cross-filter + truncated-label tooltip via event delegation on the scroll
    // container. This works for dynamically swapped rows during virtual scroll.
    merged.selectAll('.breakdown-scroll').each((_: any, i: number, scrollNodes: any) => {
      const scrollNode = scrollNodes[i] as HTMLElement;

      scrollNode.addEventListener('mouseover', (event: Event) => {
        const target = event.target as HTMLElement;
        // Truncated label tooltip (for dynamically rendered rows)
        const labelEl = target.closest('.truncated-label') as HTMLElement;
        if (labelEl && labelEl.closest('.breakdown-scroll') === scrollNode) {
          if (labelEl.scrollWidth > labelEl.offsetWidth) {
            this.dismissLinkTooltip();
            const rect = labelEl.getBoundingClientRect();
            this.linkTooltipEl = d3.select('body').append('div')
              .style('position', 'fixed').style('z-index', '10001')
              .style('pointer-events', 'none').style('background', '#0f172a')
              .style('color', '#f8fafc').style('border-radius', '8px')
              .style('padding', '6px 12px').style('font-family', "'Poppins', sans-serif")
              .style('font-size', '12px').style('font-weight', '600')
              .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
              .style('white-space', 'nowrap')
              .style('left', `${rect.left}px`).style('top', `${rect.top - 32}px`)
              .text(labelEl.textContent || '');
          }
        }
        // Cross-filter hover (same logic for regular and super cards)
        const row = target.closest('.breakdown-row') as HTMLElement;
        if (row && row.closest('.breakdown-scroll') === scrollNode) {
          const label = row.getAttribute('data-label');
          const cardEl = scrollNode.closest('.funnel-card') as HTMLElement;
          const d = d3.select(cardEl).datum() as any;
          const activeFilter = this.expandedCards.get(d.id);
          if (!activeFilter || !label) return;

          // For super cards, the field comes from the node; for regular cards, from breakdownDimensions
          const dim = d.isSuperCard
            ? { field: d.field }
            : this.props.breakdownDimensions.find((bd: any) => bd.label === activeFilter);
          if (!dim) return;
          this.crossFilterHover = label;
          this.crossFilterCardId = d.id;
          this.crossFilterField = dim.field;
          this.applyCrossFilter();
        }
      });

      scrollNode.addEventListener('mouseout', (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.closest('.truncated-label')) {
          this.dismissLinkTooltip();
        }
        const row = target.closest('.breakdown-row') as HTMLElement;
        if (row) {
          const related = (event as MouseEvent).relatedTarget as HTMLElement;
          if (!related || !row.contains(related)) {
            // Clean up cross-filter hover (same for regular and super cards)
            this.crossFilterHover = null;
            if (!this.crossFilterValue) {
              this.crossFilterCardId = null;
              this.crossFilterField = null;
            }
            this.applyCrossFilter();
          }
        }
      });

      scrollNode.addEventListener('click', (event: Event) => {
        const target = event.target as HTMLElement;
        const row = target.closest('.breakdown-row') as HTMLElement;
        if (!row || row.closest('.breakdown-scroll') !== scrollNode) return;
        event.stopPropagation();
        const label = row.getAttribute('data-label');
        const cardEl = scrollNode.closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        const activeFilter = this.expandedCards.get(d.id);
        if (!activeFilter || !label) return;

        // Toggle active row class
        const prevActive = cardEl.querySelector('.breakdown-row--active');
        if (prevActive) prevActive.classList.remove('breakdown-row--active');

        // Same cross-filter toggle for both regular and super cards
        const dim = d.isSuperCard
          ? { field: d.field }
          : this.props.breakdownDimensions.find((bd: any) => bd.label === activeFilter);
        if (!dim) return;
        if (this.crossFilterValue === label && this.crossFilterCardId === d.id) {
          this.crossFilterValue = null;
          this.crossFilterCardId = null;
          this.crossFilterField = null;
        } else {
          this.crossFilterValue = label;
          this.crossFilterCardId = d.id;
          this.crossFilterField = dim.field;
          row.classList.add('breakdown-row--active');
        }
        this.applyCrossFilter();
      });

      // Virtual scroll: swap visible rows as the user scrolls
      const ROW_H = 28;
      const VIRT_BUFFER = 10;
      scrollNode.addEventListener('scroll', () => {
        const cardEl = scrollNode.closest('.funnel-card') as HTMLElement;
        const d = d3.select(cardEl).datum() as any;
        const activeFilter = this.expandedCards.get(d.id);
        if (!activeFilter) return;
        const allItems = (this.breakdownData[d.id] || {})[activeFilter] || [];
        const searchTerm = this.cardSearchTerms.get(d.id) || '';
        const items = searchTerm
          ? allItems.filter((item: any) => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
          : allItems;
        if (items.length <= VIRT_BUFFER * 2) return;

        const scrollTop = scrollNode.scrollTop;
        const startIdx = Math.max(0, Math.floor(scrollTop / ROW_H) - VIRT_BUFFER);
        const endIdx = Math.min(items.length, startIdx + VIRT_BUFFER * 3);

        const topSpacer = scrollNode.querySelector('.breakdown-spacer-top') as HTMLElement;
        const bottomSpacer = scrollNode.querySelector('.breakdown-spacer-bottom') as HTMLElement;
        if (!topSpacer || !bottomSpacer) return;

        topSpacer.style.height = `${startIdx * ROW_H}px`;
        bottomSpacer.style.height = `${Math.max(0, (items.length - endIdx) * ROW_H)}px`;

        const existingRows = scrollNode.querySelectorAll('.breakdown-row');
        existingRows.forEach(r => r.remove());

        let rowsHtml = '';
        for (let bi = startIdx; bi < endIdx; bi++) {
          rowsHtml += renderBreakdownRow(items[bi], bi, d, ROW_H);
        }
        topSpacer.insertAdjacentHTML('afterend', rowsHtml);
      });
    });

    // Card hover highlight
    merged
      .on('mouseenter', (_event: any, d: any) => {
        this.cardsContainer.selectAll('.funnel-card')
          .filter((cd: any) => cd.id === d.id)
          .select('.card-inner')
          .style('box-shadow', `0 4px 12px ${d.color}30`)
          .style('border-color', d.color);
      })
      .on('mouseleave', (_event: any, d: any) => {
        const isExpanded = this.expandedCards.has(d.id);
        this.cardsContainer.selectAll('.funnel-card')
          .filter((cd: any) => cd.id === d.id)
          .select('.card-inner')
          .style('box-shadow', '0 1px 3px rgba(0,0,0,0.05)')
          .style('border-color', isExpanded ? d.color + '40' : '#e2e8f0');
      });

    (merged as d3.Selection<HTMLDivElement, any, any, any>)
      .call(d3.drag<HTMLDivElement, any>()
        .filter((event: any) => !event.target.closest('.card-chevron') && !event.target.closest('.card-expanded-section'))
        .on('start', function(this: HTMLDivElement) {
          self.bringCardToFront(d3.select(this));
        })
        .on('drag', function(this: HTMLDivElement, event: any, d: any) {
          const CARD_STICKY_PX = 8;
          const oldY = d.y;
          let newY = d.y + event.dy;
          if (newY < 0) newY = 0;
          if (newY > self.chartHeight - self.cardHeight) newY = self.chartHeight - self.cardHeight;

          // ── Sticky pull: snap card Y so that one of its links becomes straight ──
          // For each link touching this card, compute the card Y that would make
          // that link straight. If the tentative newY is within CARD_STICKY_PX of
          // any candidate, snap to it (closest candidate wins).
          {
            const cardId = d.id;
            // Find every link touching this card and compute the OTHER end's port Y
            // and what THIS card's contribution would need to be for straightness.
            const candidates: number[] = [];

            // For outgoing links: we need this card's source port Y to match the
            // target node's port Y. The source port Y at newY can be:
            //   - linkSourcePortOverrides[linkKey] (already shifted by oldY's
            //     delta in our existing code) — but that's a per-link override,
            //     so we can override the override. To compute the candidate, we
            //     need to know what it WOULD be at newY: existingOverride + (newY - oldY)
            //   - cardSourcePortOverrides[cardId] (similar shift logic)
            //   - default slot: newY + portPad + idx * spacing
            // For each link, compute the candidate newY that makes source == target.
            self.masterLinks.forEach((l: any) => {
              if (l.source === cardId) {
                const lk = `${l.source}->${l.target}`;
                const targetY = self.linkTargetPortOverrides.get(lk) ??
                  self.cardTargetPortOverrides.get(l.target) ??
                  ((l.targetNode?.y ?? 0) + self.cardHeight / 2);

                // Where would source port Y be at the tentative newY?
                const linkSrcOverride = self.linkSourcePortOverrides.get(lk);
                if (linkSrcOverride !== undefined) {
                  // Override moves with the card by (newY - oldY)
                  const projected = linkSrcOverride + (newY - oldY);
                  // We want projected == targetY → newY = oldY + (targetY - linkSrcOverride)
                  candidates.push(oldY + (targetY - linkSrcOverride));
                } else {
                  const cardSrcOverride = self.cardSourcePortOverrides.get(cardId);
                  if (cardSrcOverride !== undefined) {
                    const projected = cardSrcOverride + (newY - oldY);
                    candidates.push(oldY + (targetY - cardSrcOverride));
                  } else {
                    // Default slot Y at newY = newY + portPad + idx * spacing
                    // But we don't easily know idx here without re-running port distribution.
                    // Approximate: use card center (where bundled defaults sit and where
                    // single-link cases land).
                    const projectedAtCenter = newY + self.cardHeight / 2;
                    // Solve: newY + cardHeight/2 == targetY → newY = targetY - cardHeight/2
                    candidates.push(targetY - self.cardHeight / 2);
                    // Quiet "unused" warning — projectedAtCenter is for documentation
                    void projectedAtCenter;
                  }
                }
              }
              if (l.target === cardId) {
                const lk = `${l.source}->${l.target}`;
                const sourceY = self.linkSourcePortOverrides.get(lk) ??
                  self.cardSourcePortOverrides.get(l.source) ??
                  ((l.sourceNode?.y ?? 0) + self.cardHeight / 2);

                const linkTgtOverride = self.linkTargetPortOverrides.get(lk);
                if (linkTgtOverride !== undefined) {
                  candidates.push(oldY + (sourceY - linkTgtOverride));
                } else {
                  const cardTgtOverride = self.cardTargetPortOverrides.get(cardId);
                  if (cardTgtOverride !== undefined) {
                    candidates.push(oldY + (sourceY - cardTgtOverride));
                  } else {
                    candidates.push(sourceY - self.cardHeight / 2);
                  }
                }
              }
            });

            // Pick the candidate closest to the tentative newY, snap if within threshold
            let bestCandidate: number | null = null;
            let bestDist = Infinity;
            for (const c of candidates) {
              const dist = Math.abs(c - newY);
              if (dist < bestDist) { bestDist = dist; bestCandidate = c; }
            }
            if (bestCandidate !== null && bestDist <= CARD_STICKY_PX) {
              // Re-clamp to chart bounds after snap
              newY = Math.max(0, Math.min(self.chartHeight - self.cardHeight, bestCandidate));
            }
          }

          const actualDy = newY - oldY;
          d.y = newY;
          d3.select(this).style('top', `${d.y}px`);

          // Shift any port Y overrides that reference this card so links follow
          // the card. Port Y overrides are stored in absolute coordinates, so
          // they need the same delta the card moved.
          if (actualDy !== 0) {
            const cardId = d.id;
            // Per-card bundle override (source side)
            const cardSrcY = self.cardSourcePortOverrides.get(cardId);
            if (cardSrcY !== undefined) {
              self.cardSourcePortOverrides.set(cardId, cardSrcY + actualDy);
            }
            // Per-card bundle override (target side)
            const cardTgtY = self.cardTargetPortOverrides.get(cardId);
            if (cardTgtY !== undefined) {
              self.cardTargetPortOverrides.set(cardId, cardTgtY + actualDy);
            }
            // Per-link overrides — iterate visible links touching this card
            self.masterLinks.forEach((l: any) => {
              if (l.source === cardId) {
                const lk = `${l.source}->${l.target}`;
                const v = self.linkSourcePortOverrides.get(lk);
                if (v !== undefined) self.linkSourcePortOverrides.set(lk, v + actualDy);
              }
              if (l.target === cardId) {
                const lk = `${l.source}->${l.target}`;
                const v = self.linkTargetPortOverrides.get(lk);
                if (v !== undefined) self.linkTargetPortOverrides.set(lk, v + actualDy);
              }
            });
          }

          self.updateLinks();
          if (self.crossFilterValue || self.crossFilterHover) {
            self.applyCrossFilter();
          }
          // Re-draw persistent fan-out sub-links if a super card row is selected
        })
      );
  }

  // ── Layout: channel-aware positioning ──

  private layoutNodes(nodes: any[], masterLinks: any[]): void {
    const nodeMap = new Map<string, any>(nodes.map(n => [n.id, n]));
    const gap = this.cardHeight + 20;

    const activeColumns = this.allColumns
      .filter((c: any) => c.active)
      .sort((a: any, b: any) => a.position - b.position)
      .map((c: any) => c.id);

    const columnNodes = new Map<string, any[]>();
    activeColumns.forEach(field => {
      columnNodes.set(field, nodes.filter(n => n.columnId === field));
    });

    // Build adjacency from visible links only (both endpoints in active columns)
    const activeSet = new Set(activeColumns);
    const incomingMap = new Map<string, string[]>();
    const outgoingMap = new Map<string, string[]>();
    masterLinks.forEach(link => {
      const s = nodeMap.get(link.source);
      const t = nodeMap.get(link.target);
      if (!s || !t || !activeSet.has(s.columnId) || !activeSet.has(t.columnId)) return;
      if (!incomingMap.has(link.target)) incomingMap.set(link.target, []);
      incomingMap.get(link.target)!.push(link.source);
      if (!outgoingMap.has(link.source)) outgoingMap.set(link.source, []);
      outgoingMap.get(link.source)!.push(link.target);
    });

    const colPosMap = new Map<string, number>();
    activeColumns.forEach((field, i) => colPosMap.set(field, i));

    // ── Phase 1: Even distribution as baseline ──
    activeColumns.forEach(field => {
      this.distributeEvenly(columnNodes.get(field) || [], gap);
    });

    // ── Phase 2: Median crossing-reduction (Sugiyama-style) ──
    // Multiple sweeps: left→right then right→left
    for (let iter = 0; iter < 3; iter++) {
      // Left → right: sort by median of incoming neighbor Y
      for (let ci = 1; ci < activeColumns.length; ci++) {
        const colNodes = columnNodes.get(activeColumns[ci]) || [];
        if (colNodes.length <= 1) continue;
        this.assignMedians(colNodes, incomingMap, nodeMap);
        if (this.hasDistinctMedians(colNodes)) {
          colNodes.sort((a: any, b: any) => a._median - b._median);
          this.spreadByMedians(colNodes, gap);
        }
      }
      // Right → left: sort by median of outgoing neighbor Y
      for (let ci = activeColumns.length - 2; ci >= 0; ci--) {
        const colNodes = columnNodes.get(activeColumns[ci]) || [];
        if (colNodes.length <= 1) continue;
        this.assignMedians(colNodes, outgoingMap, nodeMap);
        if (this.hasDistinctMedians(colNodes)) {
          colNodes.sort((a: any, b: any) => a._median - b._median);
          this.spreadByMedians(colNodes, gap);
        }
      }
    }

    // ── Phase 3: Skip-link channel avoidance ──
    // Identify links that span >1 column gap — their vertical channels pass through intermediate columns
    this.avoidSkipChannels(nodes, masterLinks, activeColumns, columnNodes, colPosMap, nodeMap, gap);

    // Cleanup temp props
    nodes.forEach(n => { delete n._median; });
  }

  // Compute median Y of each node's neighbors (used for crossing reduction)
  private assignMedians(colNodes: any[], adjacencyMap: Map<string, string[]>, nodeMap: Map<string, any>): void {
    colNodes.forEach(node => {
      const neighborIds = adjacencyMap.get(node.id) || [];
      const ys = neighborIds
        .map(id => nodeMap.get(id))
        .filter(n => n && n.y !== undefined)
        .map(n => n.y + this.cardHeight / 2)
        .sort((a, b) => a - b);
      node._median = ys.length > 0 ? ys[Math.floor(ys.length / 2)] : node.y + this.cardHeight / 2;
    });
  }

  // Check if median values are actually different (skip sort if single-source column)
  private hasDistinctMedians(colNodes: any[]): boolean {
    const first = colNodes[0]._median;
    return colNodes.some(n => Math.abs(n._median - first) > 1);
  }

  // Place cards centered on their medians, enforce min gap, then center the group
  private spreadByMedians(colNodes: any[], gap: number): void {
    if (colNodes.length === 0) return;
    // Set ideal Y from median (card top = median - half card height)
    colNodes.forEach(n => { n.y = n._median - this.cardHeight / 2; });
    // Enforce minimum gap (top-down)
    for (let i = 1; i < colNodes.length; i++) {
      if (colNodes[i].y < colNodes[i - 1].y + gap) {
        colNodes[i].y = colNodes[i - 1].y + gap;
      }
    }
    this.centerColumnGroup(colNodes, gap);
  }

  // Distribute cards like CSS space-evenly: equal gaps above, between, and below
  private distributeEvenly(colNodes: any[], _gap: number): void {
    if (colNodes.length === 0) return;
    const totalCardH = colNodes.length * this.cardHeight;
    const space = (this.chartHeight - totalCardH) / (colNodes.length + 1);
    colNodes.forEach((n, i) => { n.y = space * (i + 1) + this.cardHeight * i; });
  }

  // Center a group vertically within chartHeight and clamp to bounds
  private centerColumnGroup(colNodes: any[], gap: number): void {
    if (colNodes.length === 0) return;
    const groupTop = colNodes[0].y;
    const groupBot = colNodes[colNodes.length - 1].y + this.cardHeight;
    const offset = (this.chartHeight - (groupBot - groupTop)) / 2 - groupTop;
    colNodes.forEach(n => n.y += offset);
    // Clamp: keep first card at y >= 10
    if (colNodes[0].y < 10) {
      const shift = 10 - colNodes[0].y;
      colNodes.forEach(n => n.y += shift);
    }
    // Re-enforce gaps after centering
    for (let i = 1; i < colNodes.length; i++) {
      if (colNodes[i].y < colNodes[i - 1].y + gap) {
        colNodes[i].y = colNodes[i - 1].y + gap;
      }
    }
  }

  // Phase 3: For skip-links (spanning >1 column), compute vertical channel zones
  // through intermediate columns and reposition cards to avoid them
  private avoidSkipChannels(
    nodes: any[], masterLinks: any[], activeColumns: string[],
    columnNodes: Map<string, any[]>, colPosMap: Map<string, number>,
    nodeMap: Map<string, any>, gap: number
  ): void {
    // Collect skip links and the vertical zones they occupy in intermediate columns
    const reservedByCol = new Map<string, { minY: number, maxY: number }[]>();

    masterLinks.forEach(link => {
      const s = nodeMap.get(link.source);
      const t = nodeMap.get(link.target);
      if (!s || !t) return;
      const sPos = colPosMap.get(s.columnId);
      const tPos = colPosMap.get(t.columnId);
      if (sPos === undefined || tPos === undefined) return;
      if (Math.abs(tPos - sPos) <= 1) return; // Not a skip link

      const sy = s.y + this.cardHeight / 2;
      const ty = t.y + this.cardHeight / 2;
      const zoneMinY = Math.min(sy, ty) - this.cardHeight / 2 - 10;
      const zoneMaxY = Math.max(sy, ty) + this.cardHeight / 2 + 10;

      const startP = Math.min(sPos, tPos);
      const endP = Math.max(sPos, tPos);
      for (let p = startP + 1; p < endP; p++) {
        const field = activeColumns[p];
        if (!reservedByCol.has(field)) reservedByCol.set(field, []);
        reservedByCol.get(field)!.push({ minY: zoneMinY, maxY: zoneMaxY });
      }
    });

    if (reservedByCol.size === 0) return;

    // For each intermediate column, merge overlapping zones and reposition cards into free slots
    reservedByCol.forEach((zones, field) => {
      const colNodes = columnNodes.get(field);
      if (!colNodes || colNodes.length === 0) return;

      // Merge overlapping reserved zones
      zones.sort((a, b) => a.minY - b.minY);
      const merged: { minY: number, maxY: number }[] = [];
      zones.forEach(z => {
        if (merged.length > 0 && z.minY <= merged[merged.length - 1].maxY) {
          merged[merged.length - 1].maxY = Math.max(merged[merged.length - 1].maxY, z.maxY);
        } else {
          merged.push({ minY: z.minY, maxY: z.maxY });
        }
      });

      // Compute free vertical slots (gaps between reserved zones and chart edges)
      const slots: { top: number, bottom: number }[] = [];
      let cursor = 10;
      merged.forEach(z => {
        if (z.minY > cursor + this.cardHeight) {
          slots.push({ top: cursor, bottom: z.minY });
        }
        cursor = Math.max(cursor, z.maxY);
      });
      slots.push({ top: cursor, bottom: this.chartHeight - 10 });

      // Calculate total space needed for all cards
      const totalNeeded = colNodes.length * this.cardHeight + (colNodes.length - 1) * (gap - this.cardHeight);
      const totalSlotSpace = slots.reduce((sum, s) => sum + (s.bottom - s.top), 0);

      if (totalSlotSpace < totalNeeded) {
        // Not enough room outside channels — keep even distribution, links will dash through
        return;
      }

      // Distribute cards into free slots
      // Strategy: fill largest slots first, packing cards into available space
      // Sort cards by current Y (preserve relative order)
      colNodes.sort((a: any, b: any) => a.y - b.y);

      let cardIdx = 0;
      for (const slot of slots) {
        if (cardIdx >= colNodes.length) break;
        const slotH = slot.bottom - slot.top;
        // How many cards fit in this slot?
        const maxFit = Math.floor((slotH + (gap - this.cardHeight)) / gap);
        const toPlace = Math.min(maxFit, colNodes.length - cardIdx);
        if (toPlace <= 0) continue;

        // Center the placed cards within this slot
        const blockH = toPlace * this.cardHeight + (toPlace - 1) * (gap - this.cardHeight);
        const startY = slot.top + (slotH - blockH) / 2;

        for (let i = 0; i < toPlace; i++) {
          colNodes[cardIdx].y = startY + i * gap;
          cardIdx++;
        }
      }

      // If some cards didn't fit in slots (shouldn't happen given the check), clamp them
      for (; cardIdx < colNodes.length; cardIdx++) {
        colNodes[cardIdx].y = colNodes[cardIdx - 1].y + gap;
      }
    });
  }

  // ── Render: link paths between cards ──

  private updateLinks(transition?: any): void {
    const visibleLinks = this.computeVisibleLinks();
    const visibleNodes = this.getVisibleNodes();
    const R = 6;

    // ── 1. Port distribution ──
    const outgoingByNode = new Map<string, any[]>();
    const incomingByNode = new Map<string, any[]>();
    visibleLinks.forEach(link => {
      if (!outgoingByNode.has(link.source)) outgoingByNode.set(link.source, []);
      outgoingByNode.get(link.source)!.push(link);
      if (!incomingByNode.has(link.target)) incomingByNode.set(link.target, []);
      incomingByNode.get(link.target)!.push(link);
    });
    outgoingByNode.forEach(links => links.sort((a, b) => (a.targetNode?.y || 0) - (b.targetNode?.y || 0)));
    incomingByNode.forEach(links => links.sort((a, b) => (a.sourceNode?.y || 0) - (b.sourceNode?.y || 0)));

    // Apply user-controlled slot order overrides on top of the default sort.
    // Once a card has a slot order override, links use that order; new links
    // (not in the override) are appended at the end.
    const applyOrderOverride = (linksMap: Map<string, any[]>, overrideMap: Map<string, string[]>) => {
      linksMap.forEach((links, cardId) => {
        const override = overrideMap.get(cardId);
        if (!override) return;
        const linkKeyToLink = new Map(links.map(l => [`${l.source}->${l.target}`, l]));
        const ordered: any[] = [];
        override.forEach(key => {
          const l = linkKeyToLink.get(key);
          if (l) { ordered.push(l); linkKeyToLink.delete(key); }
        });
        linkKeyToLink.forEach(l => ordered.push(l));
        links.splice(0, links.length, ...ordered);
      });
    };
    applyOrderOverride(outgoingByNode, this.cardOutgoingSlotOrder);
    applyOrderOverride(incomingByNode, this.cardIncomingSlotOrder);

    const portPad = 12;
    const getSourcePort = (d: any): number => {
      const linkKey = `${d.source}->${d.target}`;
      // 1. Per-link split override wins
      const linkOverride = this.linkSourcePortOverrides.get(linkKey);
      if (linkOverride !== undefined) return linkOverride;
      // 2. Card override (whole bundle moved) — applies to bundled non-split links
      if (d.bundled && !d._split) {
        const cardOverride = this.cardSourcePortOverrides.get(d.source);
        if (cardOverride !== undefined) return cardOverride;
      }
      // 3. Default — spread ports across the card edge (same formula for
      // both bundled and non-bundled links so each link gets its own port).
      const links = outgoingByNode.get(d.source) || [];
      const idx = links.indexOf(d);
      const count = links.length;
      if (count <= 1) return d.sourceNode.y + this.cardHeight / 2;
      const usable = this.cardHeight - portPad * 2;
      return d.sourceNode.y + portPad + idx * (usable / (count - 1));
    };
    const getTargetPort = (d: any): number => {
      const linkKey = `${d.source}->${d.target}`;
      // 1. Per-link split override wins
      const linkOverride = this.linkTargetPortOverrides.get(linkKey);
      if (linkOverride !== undefined) return linkOverride;
      // 2. Card override (whole bundle moved) — applies to bundled non-split links
      if (d.bundled && !d._split) {
        const cardOverride = this.cardTargetPortOverrides.get(d.target);
        if (cardOverride !== undefined) return cardOverride;
      }
      // 3. Default — spread ports across the card edge
      const links = incomingByNode.get(d.target) || [];
      const idx = links.indexOf(d);
      const count = links.length;
      if (count <= 1) return d.targetNode.y + this.cardHeight / 2;
      const usable = this.cardHeight - portPad * 2;
      return d.targetNode.y + portPad + idx * (usable / (count - 1));
    };

    // ── 2. Channel allocation ──
    // Group links by corridor (source right X → target left X)
    const corridorMap = new Map<string, any[]>();
    visibleLinks.forEach(link => {
      const s = link.sourceNode;
      const t = link.targetNode;
      if (!s || !t) return;
      const sRight = (s._renderX != null ? s._renderX : (s.x || 0)) + (s._renderWidth || this.cardWidth);
      const tLeft = t._renderX != null ? t._renderX : (t.x || 0);
      const corridorKey = `${sRight}_${tLeft}`;
      if (!corridorMap.has(corridorKey)) corridorMap.set(corridorKey, []);
      corridorMap.get(corridorKey)!.push(link);
    });

    corridorMap.forEach((links, key) => {
      const [leftStr, rightStr] = key.split('_');
      const leftX = parseFloat(leftStr);
      const rightX = parseFloat(rightStr);
      const margin = 20;
      const corridorLeft = leftX + margin;
      const corridorRight = rightX - margin;
      const count = links.length;
      const hasBundled = links.some((l: any) => l.bundled);

      // Sort by source port Y for consistent channel assignment
      links.sort((a: any, b: any) => getSourcePort(a) - getSourcePort(b));

      // Stable corridor key based on column IDs (for override storage)
      const firstLink = links[0];
      const corridorKey = `${firstLink.sourceNode?.columnId || ''}_${firstLink.targetNode?.columnId || ''}`;

      // Compute the default bundle/midpoint X
      const defaultMid = (corridorLeft + corridorRight) / 2;
      const corridorOverride = this.corridorChannelOverrides.get(corridorKey);
      const bundleX = corridorOverride !== undefined ? corridorOverride : defaultMid;

      // Tag links with corridor metadata for drag handlers
      links.forEach((link: any) => {
        link._corridorKey = corridorKey;
        link._corridorLeft = corridorLeft;
        link._corridorRight = corridorRight;
        link._bundleX = bundleX;
        link._split = false;
      });

      if (hasBundled) {
        // Bundled: all links share the bundle X (with override applied)
        links.forEach((link: any) => {
          const linkKey = `${link.source}->${link.target}`;
          const hasChannelOverride = this.linkChannelOverrides.has(linkKey);
          const hasSourceOverride = this.linkSourcePortOverrides.has(linkKey);
          const hasTargetOverride = this.linkTargetPortOverrides.has(linkKey);
          link._split = hasChannelOverride || hasSourceOverride || hasTargetOverride;
          link._channelX = hasChannelOverride
            ? this.linkChannelOverrides.get(linkKey)!
            : bundleX;
        });
      } else if (count === 1) {
        const link = links[0];
        const linkKey = `${link.source}->${link.target}`;
        const linkOverride = this.linkChannelOverrides.get(linkKey);
        link._channelX = linkOverride !== undefined ? linkOverride : defaultMid;
      } else {
        const spacing = (corridorRight - corridorLeft) / (count - 1);
        links.forEach((link: any, i: number) => {
          const linkKey = `${link.source}->${link.target}`;
          const linkOverride = this.linkChannelOverrides.get(linkKey);
          link._channelX = linkOverride !== undefined ? linkOverride : (corridorLeft + i * spacing);
        });
      }
    });

    // ── 3. Orthogonal pathD with rounded corners ──
    const pathD = (d: any) => {
      const s = d.sourceNode;
      const t = d.targetNode;
      if (!s || !t) return '';

      // Use render position/width if available (accounts for wider expanded/super cards)
      const sx = (s._renderX != null ? s._renderX : (s.x || 0)) + (s._renderWidth || this.cardWidth);
      const sy = getSourcePort(d);
      const tx = t._renderX != null ? t._renderX : (t.x || 0);
      const ty = getTargetPort(d);
      const cx = d._channelX || (sx + tx) / 2;

      if (Math.abs(sy - ty) < 2) {
        return `M${sx},${sy} L${tx},${ty}`;
      }

      const r = Math.min(R, Math.abs(sy - ty) / 2, Math.abs(cx - sx) / 2, Math.abs(tx - cx) / 2);
      if (r < 1) {
        return `M${sx},${sy} L${cx},${sy} L${cx},${ty} L${tx},${ty}`;
      }

      const down = ty > sy;
      return `M${sx},${sy} ` +
        `L${cx - r},${sy} ` +
        `Q${cx},${sy} ${cx},${sy + (down ? r : -r)} ` +
        `L${cx},${ty + (down ? -r : r)} ` +
        `Q${cx},${ty} ${cx + r},${ty} ` +
        `L${tx},${ty}`;
    };

    // ── 4. Occlusion check ──
    const isLinkOccluded = (d: any): boolean => {
      const s = d.sourceNode;
      const t = d.targetNode;
      if (!s || !t) return false;

      const sx = (s.x || 0) + this.cardWidth;
      const sy = getSourcePort(d);
      const tx = t.x || 0;
      const ty = getTargetPort(d);
      const cx = d._channelX || (sx + tx) / 2;
      const pad = 4;

      for (const node of visibleNodes) {
        if (node.id === s.id || node.id === t.id) continue;
        const cLeft = node.x - pad;
        const cRight = node.x + this.cardWidth + pad;
        const cTop = node.y - pad;
        const cBottom = node.y + this.cardHeight + pad;

        // Horizontal segment: source → channel
        if (sy > cTop && sy < cBottom && sx < cRight && cx > cLeft) return true;
        // Vertical segment: channel
        const minY = Math.min(sy, ty);
        const maxY = Math.max(sy, ty);
        if (cx > cLeft && cx < cRight && minY < cBottom && maxY > cTop) return true;
        // Horizontal segment: channel → target
        if (ty > cTop && ty < cBottom && cx < cRight && tx > cLeft) return true;
      }
      return false;
    };

    // ── 5. Render ──
    const paths = this.linkGroup.selectAll('.linkPath')
      .data(visibleLinks, (d: any) => `${d.source}-${d.target}`);

    paths.exit()
      .transition().duration(50).ease(d3.easeCubicIn)
      .style('opacity', 0).style('stroke-width', 0).remove();

    const hitAreas = this.linkGroup.selectAll('.linkHitArea')
      .data(visibleLinks, (d: any) => `${d.source}-${d.target}`);
    hitAreas.exit().remove();

    const hitEnter = hitAreas.enter().append('path')
      .attr('class', 'linkHitArea')
      .attr('d', pathD)
      .style('fill', 'none')
      .style('stroke', 'transparent')
      .style('stroke-width', 14)
      .style('pointer-events', 'stroke')
      .style('cursor', 'pointer');
    const hitMerged = hitEnter.merge(hitAreas);
    hitMerged.attr('d', pathD);

    // Split links render at full opacity (like fixed links) so they're clearly distinct from the bundle
    const defaultOpacity = (d: any) => (d.bundled && !d._split) ? 0.15 : 0.6;

    const enter = paths.enter().append('path')
      .attr('class', 'linkPath')
      .attr('d', pathD)
      .style('fill', 'none')
      .style('stroke', (d: any) => d.targetNode?.color || '#a0aec0')
      .style('stroke-width', 1.5)
      .style('opacity', 0)
      .style('pointer-events', 'none');

    enter.each(function(this: SVGPathElement, d: any) {
      const path = d3.select(this);
      const totalLength = this.getTotalLength();
      path
        .style('stroke-dasharray', `${totalLength} ${totalLength}`)
        .style('stroke-dashoffset', `${totalLength}`)
        .style('opacity', defaultOpacity(d))
        .transition().duration(500).ease(d3.easeCubicOut)
        .style('stroke-dashoffset', '0');
    });

    const merged = enter.merge(paths);

    if (transition) {
      merged.transition(transition).ease(d3.easeCubicInOut)
        .attr('d', pathD)
        .style('stroke', (d: any) => d.targetNode?.color || '#a0aec0')
        .style('stroke-dashoffset', '0')
        .style('opacity', defaultOpacity);
    } else {
      merged
        .attr('d', pathD)
        .style('stroke', (d: any) => d.targetNode?.color || '#a0aec0')
        .style('stroke-dashoffset', '0')
        .style('opacity', defaultOpacity);
    }

    // Apply occlusion dash (solid on initial load if layout is clean, dashed after user drags)
    merged.style('stroke-dasharray', (d: any) => isLinkOccluded(d) ? '6 4' : 'none');

    // ── 6. Hover ──
    hitMerged
      .on('mouseenter', (_event: any, d: any) => {
        if (this.isDraggingLink) return;

        // When a cross-filter is active, skip highlight + tooltip for dimmed links
        const activeFilterValue = self.crossFilterHover || self.crossFilterValue;
        const linkKey = `${d.source}->${d.target}`;
        const filteredVol = self.crossFilterVolMap?.get(linkKey);
        if (activeFilterValue && (filteredVol === undefined || filteredVol <= 0)) return;

        // Highlight the hovered link
        this.linkGroup.selectAll('.linkPath')
          .filter((ld: any) => ld.source === d.source && ld.target === d.target)
          .style('stroke-width', 3)
          .style('opacity', 1)
          .style('stroke-dasharray', 'none');
        this.highlightCard(d.source, true);
        this.highlightCard(d.target, true);

        // Show link tooltip
        this.dismissLinkTooltip();
        const sourceName = d.sourceNode?.title || '';
        const targetName = d.targetNode?.title || '';
        const vol = d.vol || 0;

        let tooltipHtml: string;
        if (filteredVol !== undefined && filteredVol > 0 && activeFilterValue) {
          tooltipHtml = `
            <div style="font-weight:600;color:${d.sourceNode?.color || '#f8fafc'};margin-bottom:4px;">${sourceName}</div>
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px;">
              <svg width="16" height="10" viewBox="0 0 16 10"><path d="M0 5h12M10 1l4 4-4 4" stroke="#8A98AB" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span style="font-size:15px;font-weight:700;color:#ffffff;">${filteredVol.toLocaleString()}</span>
              <span style="color:#8A98AB;">of ${vol.toLocaleString()} patients</span>
            </div>
            <div style="font-size:10px;color:#8A98AB;margin-bottom:4px;">${activeFilterValue}</div>
            <div style="font-weight:600;color:${d.targetNode?.color || '#f8fafc'};">${targetName}</div>
          `;
        } else {
          tooltipHtml = `
            <div style="font-weight:600;color:${d.sourceNode?.color || '#f8fafc'};margin-bottom:4px;">${sourceName}</div>
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <svg width="16" height="10" viewBox="0 0 16 10"><path d="M0 5h12M10 1l4 4-4 4" stroke="#8A98AB" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span style="font-size:15px;font-weight:700;color:#ffffff;">${vol.toLocaleString()}</span>
              <span style="color:#8A98AB;">patients</span>
            </div>
            <div style="font-weight:600;color:${d.targetNode?.color || '#f8fafc'};">${targetName}</div>
          `;
        }

        this.linkTooltipEl = d3.select('body').append('div')
          .style('position', 'fixed')
          .style('z-index', '10001')
          .style('pointer-events', 'none')
          .style('background', '#0f172a')
          .style('color', '#f8fafc')
          .style('border-radius', '8px')
          .style('padding', '10px 14px')
          .style('font-family', "'Poppins', sans-serif")
          .style('font-size', '12px')
          .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
          .style('white-space', 'nowrap')
          .html(tooltipHtml);

        const tooltipNode = (this.linkTooltipEl.node() as HTMLElement);
        const rect = tooltipNode.getBoundingClientRect();
        this.linkTooltipEl
          .style('left', `${_event.clientX - rect.width / 2}px`)
          .style('top', `${_event.clientY - rect.height - 12}px`);
      })
      .on('mousemove', (_event: any, d: any) => {
        if (this.isDraggingLink) return;

        // Tooltip position update (preserved)
        if (this.linkTooltipEl) {
          const tooltipNode = (this.linkTooltipEl.node() as HTMLElement);
          if (tooltipNode) {
            const rect = tooltipNode.getBoundingClientRect();
            this.linkTooltipEl
              .style('left', `${_event.clientX - rect.width / 2}px`)
              .style('top', `${_event.clientY - rect.height - 12}px`);
          }
        }

        // Cursor feedback: detect closest segment and pick the appropriate cursor.
        const [px, py] = d3.pointer(_event, this.svg.node());
        const sx = (d.sourceNode?._renderX != null ? d.sourceNode._renderX : (d.sourceNode?.x || 0)) + (d.sourceNode?._renderWidth || this.cardWidth);
        const tx = d.targetNode?._renderX != null ? d.targetNode._renderX : (d.targetNode?.x || 0);
        const sy = (() => {
          const lk = `${d.source}->${d.target}`;
          const lo = this.linkSourcePortOverrides.get(lk);
          if (lo !== undefined) return lo;
          if (d.bundled && !d._split) {
            const co = this.cardSourcePortOverrides.get(d.source);
            if (co !== undefined) return co;
          }
          return d.sourceNode.y + this.cardHeight / 2;
        })();
        const ty = (() => {
          const lk = `${d.source}->${d.target}`;
          const lo = this.linkTargetPortOverrides.get(lk);
          if (lo !== undefined) return lo;
          if (d.bundled && !d._split) {
            const co = this.cardTargetPortOverrides.get(d.target);
            if (co !== undefined) return co;
          }
          return d.targetNode.y + this.cardHeight / 2;
        })();
        const cx = d._channelX || 0;
        const distSeg = (x1: number, y1: number, x2: number, y2: number): number => {
          const ddx = x2 - x1, ddy = y2 - y1;
          const lenSq = ddx * ddx + ddy * ddy;
          let t = lenSq === 0 ? 0 : ((px - x1) * ddx + (py - y1) * ddy) / lenSq;
          t = Math.max(0, Math.min(1, t));
          const cxp = x1 + t * ddx, cyp = y1 + t * ddy;
          return Math.hypot(px - cxp, py - cyp);
        };
        const distTrunk = distSeg(cx, sy, cx, ty);
        const distSrcH = distSeg(sx, sy, cx, sy);
        const distTgtH = distSeg(cx, ty, tx, ty);
        const minDist = Math.min(distTrunk, distSrcH, distTgtH);
        const cursor = (minDist === distTrunk) ? 'ew-resize' : 'ns-resize';
        d3.select(_event.currentTarget as Element).style('cursor', cursor);
      })
      .on('mouseleave', (_event: any, d: any) => {
        if (this.isDraggingLink) return;

        // If a cross-filter is active, restore to the cross-filter's opacity
        // (not the normal default) so dimmed links stay dimmed.
        const activeFilter = self.crossFilterHover || self.crossFilterValue;
        if (activeFilter && self.crossFilterVolMap) {
          const lk = `${d.source}->${d.target}`;
          const filterCardId = self.crossFilterCardId;
          const touchesCard = (d.source === filterCardId || d.target === filterCardId);
          const vol = self.crossFilterVolMap.get(lk) || 0;
          const cfOpacity = !touchesCard ? 0.08 : (vol > 0 ? 0.9 : 0.05);
          const cfWidth = vol > 0 ? 2.5 : 1.5;
          this.linkGroup.selectAll('.linkPath')
            .filter((ld: any) => ld.source === d.source && ld.target === d.target)
            .style('stroke-width', cfWidth)
            .style('opacity', cfOpacity)
            .style('stroke-dasharray', (ld: any) => isLinkOccluded(ld) ? '6 4' : 'none');
        } else {
          this.linkGroup.selectAll('.linkPath')
            .filter((ld: any) => ld.source === d.source && ld.target === d.target)
            .style('stroke-width', 1.5)
            .style('opacity', (ld: any) => (ld.bundled && !ld._split) ? 0.15 : 0.6)
            .style('stroke-dasharray', (ld: any) => isLinkOccluded(ld) ? '6 4' : 'none');
        }

        this.highlightCard(d.source, false);
        this.highlightCard(d.target, false);
        this.dismissLinkTooltip();
        d3.select(_event.currentTarget as Element).style('cursor', 'grab');
      });

    // ── 7. Drag (reroute link channels and ports) ──
    const SNAP_THRESHOLD_X = 25;     // px from bundle X to snap-rebundle channel X
    const SNAP_THRESHOLD_Y = 15;     // px from bundle port Y to snap-rebundle port Y
    const STICKY_SNAP_PX = 8;        // pull-to-straight strength during drag
    const STRAIGHT_SNAP_PX = 12;     // pull-to-straight strength on release
    const PORT_PAD = 12;             // matches portPad above; min distance from card edge
    const self = this;

    // Distance from a point to a line segment
    const distToSegment = (px: number, py: number, x1: number, y1: number, x2: number, y2: number): number => {
      const dx = x2 - x1, dy = y2 - y1;
      const lenSq = dx * dx + dy * dy;
      let t = lenSq === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const cxp = x1 + t * dx, cyp = y1 + t * dy;
      return Math.hypot(px - cxp, py - cyp);
    };

    // Helper to recompute and apply path D for a set of links
    const updateLinkPaths = (linksToUpdate: any[]) => {
      const linkSet = new Set(linksToUpdate.map(l => `${l.source}-${l.target}`));
      this.linkGroup.selectAll('.linkPath')
        .filter((d: any) => linkSet.has(`${d.source}-${d.target}`))
        .attr('d', pathD)
        .style('opacity', (d: any) => (d.bundled && !d._split) ? 0.15 : 0.6);
      this.linkGroup.selectAll('.linkHitArea')
        .filter((d: any) => linkSet.has(`${d.source}-${d.target}`))
        .attr('d', pathD);
    };

    (hitMerged as d3.Selection<SVGPathElement, any, any, any>)
      .style('cursor', 'grab')
      .call(d3.drag<SVGPathElement, any>()
        .filter((event: any) => !event.button && !event.ctrlKey)
        .on('start', function(this: SVGPathElement, event: any, d: any) {
          self.isDraggingLink = true;
          self.dismissLinkTooltip();
          d3.select(this).style('cursor', 'grabbing');

          // Get pointer in SVG coordinates
          const [px, py] = d3.pointer(event, self.svg.node());

          // Compute current geometry
          const sx = (d.sourceNode?.x || 0) + self.cardWidth;
          const sy = getSourcePort(d);
          const tx = d.targetNode?.x || 0;
          const ty = getTargetPort(d);
          const cx = d._channelX || 0;

          // Closest-segment detection
          const distTrunk = distToSegment(px, py, cx, sy, cx, ty);
          const distSrcH = distToSegment(px, py, sx, sy, cx, sy);
          const distTgtH = distToSegment(px, py, cx, ty, tx, ty);
          const minDist = Math.min(distTrunk, distSrcH, distTgtH);
          let segment: 'trunk' | 'sourceHoriz' | 'targetHoriz';
          if (minDist === distTrunk) segment = 'trunk';
          else if (minDist === distSrcH) segment = 'sourceHoriz';
          else segment = 'targetHoriz';

          // Determine drag mode and affected links
          const isCorridor = d.bundled && !d._split;
          if (isCorridor) {
            d._dragMode = 'corridor';
            d._dragLinks = visibleLinks.filter((l: any) =>
              l._corridorKey === d._corridorKey && l.bundled && !l._split
            );
          } else {
            d._dragMode = 'single';
            d._dragLinks = [d];
            if (d.bundled) {
              // Mark as split immediately so it visually separates from bundle
              d._split = true;
              const linkKey = `${d.source}->${d.target}`;
              self.linkChannelOverrides.set(linkKey, d._channelX);
            }
          }

          // Stash drag start state
          d._dragSegment = segment;
          d._dragStartChannelX = d._channelX;
          d._dragStartSourcePortY = sy;
          d._dragStartTargetPortY = ty;
          d._dragStartPointerX = px;
          d._dragStartPointerY = py;

          // For horizontal drags in single mode, hold onto the affected sibling
          // links array (BY REFERENCE — not a copy) so mutations during drag
          // are visible to getSourcePort/getTargetPort which read the same array.
          if (d._dragMode === 'single' && segment === 'sourceHoriz') {
            d._affectedSourceLinks = outgoingByNode.get(d.source) || [];
            if (!self.cardOutgoingSlotOrder.has(d.source)) {
              self.cardOutgoingSlotOrder.set(d.source,
                d._affectedSourceLinks.map((l: any) => `${l.source}->${l.target}`));
            }
          } else if (d._dragMode === 'single' && segment === 'targetHoriz') {
            d._affectedTargetLinks = incomingByNode.get(d.target) || [];
            if (!self.cardIncomingSlotOrder.has(d.target)) {
              self.cardIncomingSlotOrder.set(d.target,
                d._affectedTargetLinks.map((l: any) => `${l.source}->${l.target}`));
            }
          }
        })
        .on('drag', function(this: SVGPathElement, event: any, d: any) {
          const [px, py] = d3.pointer(event, self.svg.node());
          const dx = px - d._dragStartPointerX;
          const dy = py - d._dragStartPointerY;
          const linkKey = `${d.source}->${d.target}`;

          // X axis (channel X) — always active
          let newChannelX = d._dragStartChannelX + dx;
          newChannelX = Math.max(d._corridorLeft, Math.min(d._corridorRight, newChannelX));
          if (d._dragMode === 'corridor') {
            self.corridorChannelOverrides.set(d._corridorKey, newChannelX);
            d._dragLinks.forEach((l: any) => {
              l._channelX = newChannelX;
              l._bundleX = newChannelX;
            });
          } else {
            self.linkChannelOverrides.set(linkKey, newChannelX);
            d._channelX = newChannelX;
          }

          // Y axis — depends on grabbed segment
          if (d._dragSegment === 'sourceHoriz') {
            const minY = d.sourceNode.y + PORT_PAD;
            const maxY = d.sourceNode.y + self.cardHeight - PORT_PAD;
            let newSourceY = Math.max(minY, Math.min(maxY, d._dragStartSourcePortY + dy));

            // Sticky pull toward target Y so the link wants to be straight
            const otherY = getTargetPort(d);
            if (Math.abs(newSourceY - otherY) <= STICKY_SNAP_PX) newSourceY = otherY;

            if (d._dragMode === 'corridor') {
              self.cardSourcePortOverrides.set(d.source, newSourceY);
            } else {
              self.linkSourcePortOverrides.set(linkKey, newSourceY);
            }

            // Live slot reorder while inside the source card
            if (d._dragMode === 'single') {
              const links = d._affectedSourceLinks as any[];
              const count = links.length;
              if (count > 1) {
                const order = self.cardOutgoingSlotOrder.get(d.source)!;
                const currentIdx = order.indexOf(linkKey);
                if (currentIdx !== -1) {
                  // Convert cursor Y → slot index using the same formula getSourcePort uses
                  const usable = self.cardHeight - PORT_PAD * 2;
                  const slotSpacing = usable / (count - 1);
                  const relativeY = newSourceY - d.sourceNode.y - PORT_PAD;
                  let newIdx = Math.round(relativeY / slotSpacing);
                  newIdx = Math.max(0, Math.min(count - 1, newIdx));
                  if (newIdx !== currentIdx) {
                    order.splice(currentIdx, 1);
                    order.splice(newIdx, 0, linkKey);
                    // Reorder outgoingByNode in place so other links use the new slots
                    const linkKeyToLink = new Map(links.map(l => [`${l.source}->${l.target}`, l]));
                    links.splice(0, links.length,
                      ...order.map(k => linkKeyToLink.get(k)).filter(Boolean) as any[]);
                  }
                }
              }
            }
          } else if (d._dragSegment === 'targetHoriz') {
            const minY = d.targetNode.y + PORT_PAD;
            const maxY = d.targetNode.y + self.cardHeight - PORT_PAD;
            let newTargetY = Math.max(minY, Math.min(maxY, d._dragStartTargetPortY + dy));

            // Sticky pull toward source Y
            const otherY = getSourcePort(d);
            if (Math.abs(newTargetY - otherY) <= STICKY_SNAP_PX) newTargetY = otherY;

            if (d._dragMode === 'corridor') {
              self.cardTargetPortOverrides.set(d.target, newTargetY);
            } else {
              self.linkTargetPortOverrides.set(linkKey, newTargetY);
            }

            // Live slot reorder while inside the target card
            if (d._dragMode === 'single') {
              const links = d._affectedTargetLinks as any[];
              const count = links.length;
              if (count > 1) {
                const order = self.cardIncomingSlotOrder.get(d.target)!;
                const currentIdx = order.indexOf(linkKey);
                if (currentIdx !== -1) {
                  const usable = self.cardHeight - PORT_PAD * 2;
                  const slotSpacing = usable / (count - 1);
                  const relativeY = newTargetY - d.targetNode.y - PORT_PAD;
                  let newIdx = Math.round(relativeY / slotSpacing);
                  newIdx = Math.max(0, Math.min(count - 1, newIdx));
                  if (newIdx !== currentIdx) {
                    order.splice(currentIdx, 1);
                    order.splice(newIdx, 0, linkKey);
                    const linkKeyToLink = new Map(links.map(l => [`${l.source}->${l.target}`, l]));
                    links.splice(0, links.length,
                      ...order.map(k => linkKeyToLink.get(k)).filter(Boolean) as any[]);
                  }
                }
              }
            }
          }
          // 'trunk' segment: no Y update

          // Build the set of links to redraw: dragged links plus any siblings
          // affected by slot reorder (so the shuffle is visible).
          const seenKeys = new Set<string>();
          const linksToUpdate: any[] = [];
          const pushUnique = (l: any) => {
            const k = `${l.source}->${l.target}`;
            if (!seenKeys.has(k)) { seenKeys.add(k); linksToUpdate.push(l); }
          };
          d._dragLinks.forEach(pushUnique);
          if (d._affectedSourceLinks) d._affectedSourceLinks.forEach(pushUnique);
          if (d._affectedTargetLinks) d._affectedTargetLinks.forEach(pushUnique);
          updateLinkPaths(linksToUpdate);
        })
        .on('end', function(this: SVGPathElement, _event: any, d: any) {
          d3.select(this).style('cursor', 'grab');
          const linkKey = `${d.source}->${d.target}`;

          if (d._dragMode === 'single') {
            // Channel X snap-rebundle
            const bundleX = d._bundleX;
            if (Math.abs(d._channelX - bundleX) <= SNAP_THRESHOLD_X) {
              self.linkChannelOverrides.delete(linkKey);
              d._channelX = bundleX;
            }

            // Source port Y snap-rebundle (only if user dragged the source horizontal)
            if (d._dragSegment === 'sourceHoriz') {
              const bundleSourceY = self.cardSourcePortOverrides.get(d.source) ??
                (d.sourceNode.y + self.cardHeight / 2);
              const currentSourceY = self.linkSourcePortOverrides.get(linkKey);
              if (currentSourceY !== undefined && Math.abs(currentSourceY - bundleSourceY) <= SNAP_THRESHOLD_Y) {
                self.linkSourcePortOverrides.delete(linkKey);
              }
            }

            // Target port Y snap-rebundle (only if user dragged the target horizontal)
            if (d._dragSegment === 'targetHoriz') {
              const bundleTargetY = self.cardTargetPortOverrides.get(d.target) ??
                (d.targetNode.y + self.cardHeight / 2);
              const currentTargetY = self.linkTargetPortOverrides.get(linkKey);
              if (currentTargetY !== undefined && Math.abs(currentTargetY - bundleTargetY) <= SNAP_THRESHOLD_Y) {
                self.linkTargetPortOverrides.delete(linkKey);
              }
            }

            // ── Prefer-straight release snap ──
            // If the resulting source/target Ys are nearly equal (within
            // STRAIGHT_SNAP_PX), snap them to be exactly equal so the link
            // ends up perfectly straight.
            const wasHorizontalDrag = d._dragSegment === 'sourceHoriz' || d._dragSegment === 'targetHoriz';
            if (wasHorizontalDrag) {
              const finalSourceY = self.linkSourcePortOverrides.get(linkKey) ??
                self.cardSourcePortOverrides.get(d.source) ??
                (d.sourceNode.y + self.cardHeight / 2);
              const finalTargetY = self.linkTargetPortOverrides.get(linkKey) ??
                self.cardTargetPortOverrides.get(d.target) ??
                (d.targetNode.y + self.cardHeight / 2);
              if (Math.abs(finalSourceY - finalTargetY) <= STRAIGHT_SNAP_PX) {
                const snapY = (finalSourceY + finalTargetY) / 2;
                self.linkSourcePortOverrides.set(linkKey, snapY);
                self.linkTargetPortOverrides.set(linkKey, snapY);
              }
            }

            // ── Snap to slot on release (inside-card finish) ──
            // If the user released the drag while still inside the card AND a
            // slot order exists for that card, clear the per-link Y override so
            // the link snaps to its new slot Y from the reordered array.
            if (d._dragSegment === 'sourceHoriz' && self.cardOutgoingSlotOrder.has(d.source)) {
              const finalY = self.linkSourcePortOverrides.get(linkKey);
              const sCardTop = d.sourceNode.y;
              const sCardBottom = d.sourceNode.y + self.cardHeight;
              if (finalY !== undefined && finalY >= sCardTop && finalY <= sCardBottom) {
                self.linkSourcePortOverrides.delete(linkKey);
              }
            }
            if (d._dragSegment === 'targetHoriz' && self.cardIncomingSlotOrder.has(d.target)) {
              const finalY = self.linkTargetPortOverrides.get(linkKey);
              const tCardTop = d.targetNode.y;
              const tCardBottom = d.targetNode.y + self.cardHeight;
              if (finalY !== undefined && finalY >= tCardTop && finalY <= tCardBottom) {
                self.linkTargetPortOverrides.delete(linkKey);
              }
            }

            // Recompute _split based on whether ANY overrides remain
            const hasChannelOverride = self.linkChannelOverrides.has(linkKey);
            const hasSourceOverride = self.linkSourcePortOverrides.has(linkKey);
            const hasTargetOverride = self.linkTargetPortOverrides.has(linkKey);
            d._split = hasChannelOverride || hasSourceOverride || hasTargetOverride;

            // Animate snap on the dragged link AND any sibling links that
            // were reordered, so the final positions land smoothly.
            const animKeys = new Set<string>();
            animKeys.add(linkKey);
            if (d._affectedSourceLinks) {
              d._affectedSourceLinks.forEach((l: any) => animKeys.add(`${l.source}->${l.target}`));
            }
            if (d._affectedTargetLinks) {
              d._affectedTargetLinks.forEach((l: any) => animKeys.add(`${l.source}->${l.target}`));
            }
            const linkSelector = self.linkGroup.selectAll('.linkPath')
              .filter((ld: any) => animKeys.has(`${ld.source}->${ld.target}`));
            const hitSelector = self.linkGroup.selectAll('.linkHitArea')
              .filter((ld: any) => animKeys.has(`${ld.source}->${ld.target}`));
            linkSelector
              .transition().duration(180).ease(d3.easeCubicOut)
              .attr('d', pathD)
              .style('opacity', (ld: any) => (ld.bundled && !ld._split) ? 0.15 : 0.6);
            hitSelector
              .transition().duration(180).ease(d3.easeCubicOut)
              .attr('d', pathD);
          }

          // Cleanup drag state
          delete d._dragMode;
          delete d._dragLinks;
          delete d._dragSegment;
          delete d._dragStartChannelX;
          delete d._dragStartSourcePortY;
          delete d._dragStartTargetPortY;
          delete d._dragStartPointerX;
          delete d._dragStartPointerY;
          delete d._affectedSourceLinks;
          delete d._affectedTargetLinks;

          // Defer the flag reset so the click-suppressing mouseleave still sees it
          setTimeout(() => { self.isDraggingLink = false; }, 50);
        })
      );
  }

  private highlightCard(nodeId: string, highlight: boolean): void {
    const node = this.nodeMap.get(nodeId);
    if (!node) return;
    const card = this.cardsContainer.selectAll('.funnel-card')
      .filter((d: any) => d.id === nodeId)
      .select('.card-inner');
    if (highlight) {
      card
        .style('box-shadow', `0 4px 12px ${node.color}30`)
        .style('border-color', node.color);
    } else {
      const isExpanded = this.expandedCards.has(nodeId);
      card
        .style('box-shadow', '0 1px 3px rgba(0,0,0,0.05)')
        .style('border-color', isExpanded ? node.color + '40' : '#e2e8f0');
    }
  }
}
